import React, {
	useState,
	useEffect,
	useReducer,
	useCallback,
	useRef,
	Dispatch,
	useContext,
	SetStateAction,
	ReactNode,
	useMemo,
} from "react";
import { VideoJS } from "./VideoJS/VideoJS.js";
import { TimestampLinks, isValidEvent, processDisplay, processLink } from "./Timestamps/TimestampLinks.js";
import { SeekbarEvents } from "./SeekbarEvents/SeekbarEvents.js";
import useTimestamps, { VideoEvent, TimestampsArr } from "../../../utilities/useTimestamps.js";
import "./ProctoringResultView.css";
import apiClient from "../../../../helpers/api-client.js";
import { removeAssessmentNameSuffix } from "../../../../helpers/assessment-name";
import {
	CoreButton,
	CoreChip,
	CoreIconButton,
	CoreMenuButton,
	CoreMenuItem,
	CoreModal,
	CoreProgressBar,
	CoreSelect,
	CoreSpinner,
	CoreTabs,
	CoreTextBox,
	CoreTextField,
	coreToast,
	createTabsContext,
} from "@verity/components/core";
import { getPlatformTextFromProctoringOptions, useFakeProgress } from "@verity/components";
import PlayArrowRoundedIcon from "../../Resources/play.svg?react";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import FullscreenRoundedIcon from "../../Resources/fullScreen.svg?react";
import ExitFullscreenIcon from "../../Resources/exitFullscreen.svg?react";
import PlayOverlayIcon from "../../Resources/play-overlay.svg?react";
import PlayOverlayIconGlowing from "../../Resources/play-overlay-glowing.svg?react";
import ReplayIcon from "../../Resources/replay.svg?react";
import ReplayIconGlowing from "../../Resources/replay-glowing.svg?react";
import SvgIcon from "@mui/material/SvgIcon";
import PaperPlaneIcon from "../../../images/paper-plane-right.svg?react";
import FlaggedEventsIcon from "../../../../images/reports/flaggedEventsIcon.svg?react";
import NotesIcon from "../../../../images/reports/notesIcon.svg?react";
import LiveNotesIcon from "../../../../images/reports/liveNotesIcon.svg?react";
import RoomScanIcon from "../../../../images/reports/roomScanIcon.svg?react";
import IdIcon from "../../../../images/reports/idIcon.svg?react";
import WebcamIcon from "../../../../images/reports/webcamIcon.svg?react";
import MicroPhoneIcon from "../../../../images/reports/microphone.svg?react";
import MonitorIcon from "../../../../images/reports/monitor.svg?react";
import ConfigIcon from "../../../../images/reports/configIcon.svg?react";
import ArrowIcon from "../../../../images/reports/backIcon.svg?react";
import ScratchNoteIcon from "./images/scratch-notes.svg?react";
import SignatureIcon from "../../../../images/reports/signatureIcon.svg?react";
import circleLoadingGif from "../../../../images/reports/circleLoadingGif.gif";
import OverallSummaryIcon from "../../../../images/reports/overallSummaryIcon.svg?react";
import SevereIcon from "../../../../images/reports/severeIcon.svg?react";
import MajorIcon from "../../../../images/reports/majorIcon.svg?react";
import MinorIcon from "../../../../images/reports/minorIcon.svg?react";
import CloseXIcon from "../../../../images/reports/closeX.svg?react";
import { SessionInfo, LiveSessionNote, QuizInfoSummary } from "../../TestResultsWrapper.js";
import { SessionNoteRow } from "../session-notes/SessionNoteRow.js";
import { binSearch } from "../../../utilities/timestampBinSearch.js";
import { Box, Grid, Link, ListItemText, Skeleton, Typography } from "@mui/material";
import i18n from "../../../../i18n.js";
import substitutePhoto from "../../../images/profile_substitute.svg";
import Checkmark from "../../../images/checkmark-icon.svg?react";
import DownloadPDFReportButton from "../../PDFGenerator";
import { roomscanDisplayStateAtom } from "./store.js";
import ProctoringVideoPlayer, { SessionNote, VideoSettings } from "./ProctoringVideoPlayer.js";
import { DownloadRequestStatusType } from "./DownloadRequestModal.js";
import BackButton from "../../../../images/reports/backButton.svg?react";
import {
	ProctoringAssessmentConfigurationV1,
	ProctoringOption,
	Application,
	OptionRelatedInstituteSettings,
	Extension,
} from "@verity/proctoring-option/types";
import {
	categorizeProctoringOptions,
	CollapsibleList,
	getUniqueApplicationNamesAndIcons,
	PROCTORING_OPTION_TEXT,
	api,
	NestedOption,
	VerityInstituteSettings,
} from "@verity/components";
import Action from "./images/threedot.svg?react";
import { PortalControllerContext, type PortalController } from "../../../../portal/context.js";
import ScratchNote from "./tabs/ScratchNote.js";
import { useAsync } from "react-use";
import { formatEventTimestamp } from "../../ProctoringResultsPage/ProctoringEventsTable";
import {
	ZodToReadable,
	ObjectDetectionEventSchema,
	FaceDetectionEventSchema,
	FlaggedKeywordEventSchema,
} from "@yujacorp/verity-api-contract";
import { AiSummaryResponse } from "@verity/api-client/types";
import { VideoControlButton, VolumeControls, Seekbar } from "./VideoControls/VideoControls.js";
import { EllipsisTooltip } from "../../../../../../../packages/components/src/editQuiz/proctoring-options/option-modals/listEditor";
import { faceDetectionCapabilityAtom, objectDetectionCapabilityAtom } from "./overlay/DetectionOverlaySettings.js";
import { useAtom } from "jotai";
import { FlagLevelBadge, NoResults } from "../../../../reports/shared/report-components.js";
import SignatureTabPanel from "./tabs/SignaturePanel.js";
import { canAccessResource } from "../../../../helpers/privilege";

type LoggingEvents = Exclude<
	Awaited<ReturnType<typeof apiClient.proctoringSession.getSessionLoggedEvents>>,
	null | undefined
>;

// Created array so future tabs are included in tab id registration
const TAB_KEYS = ["flaggedEvents", "notes", "liveNotes"] as const;
const MAIN_TAB_KEYS = ["recording", "roomScan", "config", "idPhotos", "signature", "scratchNote"] as const;
const ALL_TAB_KEYS = [...MAIN_TAB_KEYS, ...TAB_KEYS];
type AllTabs = (typeof ALL_TAB_KEYS)[number];
type Tab = (typeof TAB_KEYS)[number];
type MainTab = (typeof MAIN_TAB_KEYS)[number];

export const { TabsProvider: ProctoringViewTabsProvider, useTabContext: useProctoringViewTabContext } =
	createTabsContext<AllTabs>();

type AutomatedProctoringSessionNote = SessionNote & {
	id: number;
};

type LiveProctoringSessionNote = SessionNote & {
	id: string;
};

async function getVideoUrl(
	sessionId: string,
	recordingType: string,
	courseId: string | number,
): Promise<{ [key: string]: string } | null> {
	try {
		return apiClient.proctoringSession.getVideoUrl({ sessionId, recordingType, courseId });
	} catch (e) {
		return null;
	}
}

export async function getRoomScanVideoUrl(sessionId: string): Promise<{ [key: string]: string } | null> {
	try {
		const url = `/api/verity/quiz/recording/${sessionId}/room_scan`;
		const response = await fetch(url, {
			mode: "cors",
			credentials: "same-origin",
		});
		if (response.status === 200) {
			const res = (await response.json()) as { [url: string]: string };
			return {
				"0": res.url,
			};
		}
		throw new Error("Unable to get room scan video");
	} catch (e) {
		return null;
	}
}

/**
 * getLoggedEvents will make an HTTP GET request to the node-server for the session with uuid: sessionId
 */
export async function getLoggedEvents(sessionId: string, courseId: number): Promise<LoggingEvents> {
	try {
		return await apiClient.proctoringSession.getSessionLoggedEvents({ sessionId, courseId });
	} catch (e) {
		return { events: [], anomalies: [] };
	}
}

function findStartDate(loggingEvents: LoggingEvents): Date {
	const startProctoringEvent = loggingEvents.events.find((e) => {
		return e && e.type === "session" && e.name === "start proctoring" && e.time && e.details?.success;
	});
	return new Date(startProctoringEvent?.time ?? loggingEvents.events?.[0].time);
}

function convertLoggingEventsToVideoEvents({
	startDate,
	loggingEvents,
}: {
	startDate?: Date;
	loggingEvents?: LoggingEvents;
}): VideoEvent[] | null {
	if (!loggingEvents?.events.length || loggingEvents.events.length === 0) {
		return null;
	}
	if (!startDate) {
		startDate = findStartDate(loggingEvents);
	}
	const videoEvents: VideoEvent[] = [];
	let forceStopProctoringEvent: VideoEvent | undefined;
	for (const loggingEvent of loggingEvents.events) {
		let anomaly: { comment?: string | null; flagLevel: number; resolved: boolean } | undefined;
		let flag = 0;
		if (loggingEvent.anomalyRef != null) {
			anomaly = loggingEvents.anomalies.find((x) => x.id === loggingEvent.anomalyRef);
			const changeFlag = anomaly?.flagLevel ?? -1;
			if (changeFlag !== -1) {
				flag = changeFlag;
			}
		}
		const time = (new Date(loggingEvent.time).getTime() - startDate.getTime()) / 1000;
		const videoEvent: VideoEvent = {
			key: `event-${loggingEvent.id}`,
			id: loggingEvent.id,
			time: time,
			name: loggingEvent.name,
			type: loggingEvent.type,
			details: loggingEvent.details,
			flag_level: flag,
			logged_time: loggingEvent.time,
			comment: anomaly?.comment ?? undefined,
			resolved: anomaly?.resolved ?? false,
		};
		videoEvents.push(videoEvent);
		if (loggingEvent.name === "force stop proctoring") {
			forceStopProctoringEvent = videoEvent;
		}
		if (loggingEvent.name === "force stop proctoring message" && forceStopProctoringEvent) {
			forceStopProctoringEvent.details.message = loggingEvent.details.message;
		}
	}
	const endProctoringEventTime = videoEvents.findLast((e) => e.type === "session" && e.name === "end proctoring")?.time;

	// add anomalies that are not referenced by events
	const anomalyRefs = loggingEvents.events
		.map((event) => event.anomalyRef)
		.filter((ref): ref is number => typeof ref === "number");
	const videoAnomalies: VideoEvent[] = [];
	for (const anomaly of loggingEvents.anomalies) {
		if (!anomalyRefs.includes(anomaly.id)) {
			// prevent duplicate anomalies events at same time
			if (
				anomaly.timeStart &&
				!videoAnomalies.find((x) => x.type === anomaly.type && x.logged_time === anomaly.timeStart)
			) {
				videoAnomalies.push({
					key: `anomaly-${anomaly.id}`,
					time: (new Date(anomaly.timeStart).getTime() - startDate.getTime()) / 1000,
					name: anomaly.type,
					type: anomaly.type,
					details: { start: true },
					flag_level: anomaly.flagLevel,
					resolved: anomaly.resolved,
					logged_time: anomaly.timeStart,
				});
			}
			if (
				anomaly.timeEnd &&
				anomaly.timeEnd !== anomaly.timeStart &&
				!videoAnomalies.find((x) => x.type === anomaly.type && x.logged_time === anomaly.timeEnd)
			) {
				videoAnomalies.push({
					key: `anomaly-${anomaly.id}`,
					time: (new Date(anomaly.timeEnd).getTime() - startDate.getTime()) / 1000,
					name: anomaly.type,
					type: anomaly.type,
					details: { start: false },
					flag_level: 0,
					resolved: false,
					logged_time: anomaly.timeEnd,
				});
			}
		}
	}
	videoEvents.push(...videoAnomalies);
	if (endProctoringEventTime) {
		return videoEvents.filter(
			(x) =>
				x.time <= endProctoringEventTime ||
				(x.type === "session" && (x.name === "no submission" || x.name === "upload incomplete")),
		);
	}
	return videoEvents;
}

async function getSessionNotes(sessionId: string, courseId: number) {
	try {
		return await apiClient.proctoringSession.getSessionNotes({ sessionId, courseId });
	} catch (e) {
		console.error(e);
	}
	throw new Error("Failed to get session note");
}

async function addSessionNote(sessionId: string, courseId: number, content: string, timestamp: Date) {
	try {
		const response = await apiClient.proctoringSession.addSessionNote({ sessionId, courseId, content, timestamp });
		if (response) {
			return true;
		}
	} catch (e) {
		console.error(e);
	}
	throw new Error("Failed to add session note");
}

async function editSessionNote(sessionId: string, courseId: number, noteId: number, content: string) {
	try {
		const response = await apiClient.proctoringSession.editSessionNote({ sessionId, courseId, noteId, content });
		if (response) {
			return true;
		}
	} catch (e) {
		console.error(e);
	}
	throw new Error("Failed to edit session note");
}

async function deleteSessionNote(sessionId: string, courseId: number, noteId: number) {
	try {
		const response = await apiClient.proctoringSession.deleteSessionNote({ sessionId, courseId, noteId });
		if (response) {
			return true;
		}
	} catch (e) {
		console.error(e);
	}
	throw new Error("Failed to delete session note");
}

async function getRoomScanNotes(sessionId: string, courseId: number) {
	try {
		return await apiClient.proctoringSession.getRoomScanNotes({ sessionId, courseId });
	} catch (e) {
		console.error(e);
	}
	throw new Error("Failed to get session note");
}

async function addRoomScanNote(sessionId: string, courseId: number, content: string, timestamp: number) {
	try {
		const response = await apiClient.proctoringSession.addRoomScanNote({ sessionId, courseId, content, timestamp });
		if (response) {
			return true;
		}
	} catch (e) {
		console.error(e);
	}
	throw new Error("Failed to add session note");
}

async function editRoomScanNote(sessionId: string, courseId: number, noteId: number, content: string) {
	try {
		const response = await apiClient.proctoringSession.editRoomScanNote({ sessionId, courseId, noteId, content });
		if (response) {
			return true;
		}
	} catch (e) {
		console.error(e);
	}
	throw new Error("Failed to edit session note");
}

async function deleteRoomScanNote(sessionId: string, courseId: number, noteId: number) {
	try {
		const response = await apiClient.proctoringSession.deleteRoomScanNote({ sessionId, courseId, noteId });
		if (response) {
			return true;
		}
	} catch (e) {
		console.error(e);
	}
	throw new Error("Failed to delete session note");
}

export interface VideoPlaybackState {
	timer: number;
	lastTime: number;
	skip: boolean;
}

interface TabType {
	key: string;
	label: string;
	icon?: React.JSX.Element;
	id?: string;
}

type PlayAction = {
	type: "play";
	speed: number;
};

type PauseAction = {
	type: "pause";
};

type SkipAction = {
	type: "skip";
	time: number;
};

export type VideoPlaybackAction = PlayAction | PauseAction | SkipAction;

const CircleWithCount = ({
	color,
	backgroundColor,
	count,
	label,
}: {
	color: string;
	backgroundColor: string;
	count?: number;
	label: string;
}) => (
	<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
		<Box
			width="100px"
			height="100px"
			borderRadius="50%"
			display="flex"
			justifyContent="center"
			alignItems="center"
			style={{ backgroundColor }}
		>
			<Typography style={{ color, fontSize: "41px", fontWeight: "700" }}>
				{count ?? <Skeleton width={25} height={60} />}
			</Typography>
		</Box>
		<Typography style={{ color: "#000000", fontSize: "18px" }}>{label}</Typography>
	</div>
);

const CircleGrid = ({
	severeCount,
	majorCount,
	minorCount,
}: {
	severeCount?: number;
	majorCount?: number;
	minorCount?: number;
}) => (
	<Grid item container height={"20%"} justifyContent={"space-between"} paddingTop={"14px"}>
		<CircleWithCount color="#D15127" backgroundColor="#D1512726" count={severeCount} label="Severe" />
		<CircleWithCount color="#BF7A12" backgroundColor="#FFF6EB" count={majorCount} label="Major" />
		<CircleWithCount color="#5E7831" backgroundColor="#2E620E26" count={minorCount} label="Minor" />
		<CircleWithCount
			color="#657CCC"
			backgroundColor="#657CCC26"
			count={
				severeCount !== undefined && majorCount !== undefined && minorCount !== undefined
					? severeCount + majorCount + minorCount
					: undefined
			}
			label={i18n.t("result.components.resultsPage.proctoringEventsTableTSX.flags")}
		/>
	</Grid>
);

function StudentOptionDisplay({ name, flagLevel }: { name: string; flagLevel: number }) {
	return (
		<Box display="flex" justifyContent="space-between" alignItems="center" gap="12px" width="100%">
			<Box
				sx={{
					overflow: "hidden",
					whiteSpace: "nowrap",
					textOverflow: "ellipsis",
					flex: 1,
				}}
			>
				{name}
			</Box>
			<FlagLevelBadge flagLevel={flagLevel} />
		</Box>
	);
}

function convertProctoringAssessmentConfigurationV1toProctoringOption(
	proctoringAssessmentConfiguration?: Partial<ProctoringAssessmentConfigurationV1>,
) {
	const proctoringOptions: Partial<ProctoringOption> = {
		videoRecordingMode: proctoringAssessmentConfiguration?.videoRecordingMode,
		audioRecordingMode: proctoringAssessmentConfiguration?.audioRecordingMode,
		recordScreen: proctoringAssessmentConfiguration?.recordScreen,
		scanRoom: proctoringAssessmentConfiguration?.scanRoom,
		recordWebTraffic: proctoringAssessmentConfiguration?.recordWebTraffic,
		detectSidePanel: proctoringAssessmentConfiguration?.detectSidePanel,
		oneScreenEnforcementMode: proctoringAssessmentConfiguration?.oneScreenEnforcementMode,
		disableNewTab: proctoringAssessmentConfiguration?.disableNewTab,
		closeOpenTabs: proctoringAssessmentConfiguration?.closeOpenTabs,
		tabWhiteList: proctoringAssessmentConfiguration?.tabWhiteList,
		disableClipboardMode: proctoringAssessmentConfiguration?.disableClipboardMode,
		enableGuidedAccessMode: proctoringAssessmentConfiguration?.enableGuidedAccessMode,
		restrictHandoffMode: proctoringAssessmentConfiguration?.restrictHandoffMode,
		showRecordingPanel: proctoringAssessmentConfiguration?.showRecordingPanel,
		idVerification: proctoringAssessmentConfiguration?.idVerification,
		idVerificationEnableAi: proctoringAssessmentConfiguration?.idVerificationEnableAi,
		faceTrackingMode: proctoringAssessmentConfiguration?.faceTrackingMode,
		objectDetectionMode: proctoringAssessmentConfiguration?.objectDetectionMode,
		enforceLockdown: proctoringAssessmentConfiguration?.enforceLockdown,
		enforceMobile: proctoringAssessmentConfiguration?.enforceMobile,
		isExtension: proctoringAssessmentConfiguration?.isExtension,
		isLockdown: proctoringAssessmentConfiguration?.isLockdown,
		isMobile: proctoringAssessmentConfiguration?.isMobile,
		isChromeOs: proctoringAssessmentConfiguration?.isChromeOs ?? proctoringAssessmentConfiguration?.isLockdown,
		recordClipboard: proctoringAssessmentConfiguration?.recordClipboard,
		recordInput: proctoringAssessmentConfiguration?.recordInput,
		excludedWebTrafficRecordingUrls: proctoringAssessmentConfiguration?.excludedWebTrafficRecordingUrls,
		disableSpellcheck: proctoringAssessmentConfiguration?.disableSpellcheck,
		disablePrinting: proctoringAssessmentConfiguration?.disablePrinting,
		disableRightClick: proctoringAssessmentConfiguration?.disableRightClick,
		extensionBlockMode: proctoringAssessmentConfiguration?.extensionBlockMode,
		allowedExtensionsList: proctoringAssessmentConfiguration?.allowedExtensionsList,
		tabBlockList: proctoringAssessmentConfiguration?.tabBlockList,
		hideOtherWindows: proctoringAssessmentConfiguration?.hideOtherWindows,
		keyboardShortcutBlockList: proctoringAssessmentConfiguration?.keyboardShortcutBlockList,
		disableDownloadFiles: proctoringAssessmentConfiguration?.disableDownloadFiles,
		applicationBlockMode: proctoringAssessmentConfiguration?.applicationBlockMode,
		blockWindowedApplications: proctoringAssessmentConfiguration?.blockWindowedApplications,
		applicationBlockList: proctoringAssessmentConfiguration?.applicationBlockList,
		allowedApplicationsList: proctoringAssessmentConfiguration?.allowedApplicationsList,
		blockVirtualMachines: proctoringAssessmentConfiguration?.blockVirtualMachines,
		recordApplicationsActivity: proctoringAssessmentConfiguration?.recordApplicationsActivity,
		quietEnvironment: proctoringAssessmentConfiguration?.quietEnvironment,
		enableCalculator: proctoringAssessmentConfiguration?.enableCalculator,
		enableWhiteboard: proctoringAssessmentConfiguration?.enableWhiteboard,
		uploadScratchNotes: proctoringAssessmentConfiguration?.uploadScratchNotes,
		idVerificationMode: proctoringAssessmentConfiguration?.idVerificationMode,
		restrictDevices: proctoringAssessmentConfiguration?.restrictDevices,
		uploadSignature: proctoringAssessmentConfiguration?.uploadSignature,
		audioTranscription: proctoringAssessmentConfiguration?.audioTranscription,
		flaggedKeywords: proctoringAssessmentConfiguration?.flaggedKeywords,
		enableGeofence: proctoringAssessmentConfiguration?.enableGeofence,
		geofenceLocations: proctoringAssessmentConfiguration?.geofenceLocations,
		disableVirtualCamera: proctoringAssessmentConfiguration?.disableVirtualCamera,
	};
	return proctoringOptions;
}

function CollapsibleApplicationList({
	allApplications,
	processNames,
}: {
	allApplications: Application[];
	processNames: string[];
}) {
	return (
		<CollapsibleList
			items={Object.entries(getUniqueApplicationNamesAndIcons(processNames, allApplications)).map(
				([name, icon], index) => (
					<div
						key={index}
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "start",
							gap: "10px",
							width: "100%",
							maxWidth: "100%",
							minWidth: 0,
						}}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								maxWidth: "100%",
								minWidth: 0,
							}}
						>
							<span style={{ minWidth: "20px" }}>{index + 1}.</span>
							<div
								key={name}
								style={{
									display: "flex",
									alignItems: "center",
									gap: "10px",
								}}
							>
								{icon && (
									<div
										style={{
											width: 24,
											height: 24,
										}}
										dangerouslySetInnerHTML={{ __html: icon }}
									/>
								)}
								<span>{name}</span>
							</div>
						</div>
					</div>
				),
			)}
		/>
	);
}

function CollapsibleExtensionList({
	allExtensions,
	customExtensions,
}: {
	allExtensions: Extension[];
	customExtensions: string[];
}) {
	// Convert string[] → Extension[]
	function convertCustomExtensions(customList: string[], allExtensions: Extension[]): Extension[] {
		return customList.map((entry) => {
			const id = entry.slice(-32);
			const name = entry.slice(0, -32);

			// Try to find preset extension by ID
			const preset = allExtensions.find((ext) => ext.id === id);

			return {
				id,
				name: preset ? preset.name : name, // prefer real name
				iconSvg: preset ? preset.iconSvg : undefined, // only use real icon if match
				type: preset ? preset.type : "Other",
			};
		});
	}

	const mergedExtensions = convertCustomExtensions(customExtensions, allExtensions);

	return (
		<CollapsibleList
			items={mergedExtensions.map((ext, index) => (
				<div
					key={index}
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "start",
						gap: "10px",
						width: "100%",
						maxWidth: "100%",
						minWidth: 0,
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							maxWidth: "100%",
							minWidth: 0,
						}}
					>
						<span style={{ minWidth: "20px" }}>{index + 1}.</span>
						<div
							key={ext.name}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "10px",
							}}
						>
							{ext.iconSvg && (
								<div
									style={{
										width: 24,
										height: 24,
									}}
									dangerouslySetInnerHTML={{ __html: ext.iconSvg }}
								></div>
							)}
							<span>{ext.name}</span>
						</div>
					</div>
				</div>
			))}
		/>
	);
}

function CheckedSubOption({ children }: { children: ReactNode }) {
	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "10px",
				lineHeight: "1.6",
			}}
		>
			<>
				<Checkmark aria-hidden={true} />
				{children}
			</>
		</div>
	);
}

const ProctoringConfigView = ({
	proctoringAssessmentConfiguration,
	applications,
	extensions,
	instituteSettings,
	verityInstituteSettings,
	optionRelatedInstituteSettings,
}: {
	proctoringAssessmentConfiguration?: Partial<ProctoringAssessmentConfigurationV1>;
	applications: Application[];
	extensions: Extension[];
	instituteSettings?: PortalController["instituteSettings"];
	verityInstituteSettings?: VerityInstituteSettings;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
}) => {
	const titleTextStyle = {
		fontWeight: "700",
		flexBasis: "35%",
		display: "flex",
		alignItems: "center",
		gap: "6px",
		fontSize: "18px",
	} as React.CSSProperties;
	const option = convertProctoringAssessmentConfigurationV1toProctoringOption(proctoringAssessmentConfiguration);

	const { optionCategories, optionChildren } = categorizeProctoringOptions(
		option,
		verityInstituteSettings,
		optionRelatedInstituteSettings,
		instituteSettings,
	);
	const { getTabIdByType } = useProctoringViewTabContext();

	const renderNestedChildren = (items: NestedOption[], optionKey?: keyof ProctoringOption) => {
		return (
			<>
				{items.map((item, index) => (
					<div
						key={index}
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "10px",
						}}
					>
						<CheckedSubOption key={optionKey ? `${optionKey}-${index + 1}` : undefined}>{item.text}</CheckedSubOption>
						{item.children && item.children.length > 0 && (
							<div style={{ marginLeft: "20px" }}>{renderNestedChildren(item.children)}</div>
						)}
					</div>
				))}
			</>
		);
	};

	const methodKey = proctoringAssessmentConfiguration?.isLiveProctored
		? "live"
		: proctoringAssessmentConfiguration?.isLivePopIn
			? "popin"
			: "automated";

	return (
		<Box maxHeight={"100%"} aria-labelledby={getTabIdByType("config")} role="tabpanel" sx={{ marginTop: "5px" }}>
			{!proctoringAssessmentConfiguration ? (
				<div
					style={{
						width: "100%",
						height: "100%",
						display: "flex",
						justifyContent: "center",
						alignContent: "center",
						marginTop: "100px",
					}}
				>
					<CoreSpinner size={100} />
				</div>
			) : (
				<div>
					{proctoringAssessmentConfiguration.thirdPartyAssessmentOptions && (
						<div style={{ marginBottom: "40px" }}>
							<Typography variant="h2" style={titleTextStyle}>
								{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.thirdPartyAssessmentDetails")}
							</Typography>
							<hr
								style={{
									lineHeight: "0.5px",
									width: "100%",
									backgroundColor: "#a6a6a6",
									margin: "6px 0",
								}}
							/>
							<Box display="flex" flexDirection="column" gap="8px" width="100%" marginTop="24px" fontSize={"14px"}>
								<div>
									{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.assessmentUrl", {
										url: proctoringAssessmentConfiguration.thirdPartyAssessmentOptions.thirdPartyUrl,
									})}
								</div>
								{proctoringAssessmentConfiguration.thirdPartyAssessmentOptions.password && (
									<div>
										{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.assessmentPassword", {
											password: proctoringAssessmentConfiguration.thirdPartyAssessmentOptions.password,
										})}
									</div>
								)}
							</Box>
						</div>
					)}
					<div style={{ marginBottom: "40px" }}>
						<Typography variant="h2" style={titleTextStyle}>
							{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.details")}
						</Typography>
						<hr style={{ lineHeight: "0.5px", width: "100%", backgroundColor: "#a6a6a6", margin: "6px 0" }} />
						<Box display="flex" flexDirection="column" gap="8px" width="100%" marginTop="24px" fontSize={"14px"}>
							<div>
								{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.platforms", {
									platforms: getPlatformTextFromProctoringOptions({
										...option,
										isChromeOs: option.isChromeOs || proctoringAssessmentConfiguration.forceChromeOsManagedGuestSession,
									}),
								})}
							</div>
							<div>
								{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.method", {
									method: i18n.t(
										`result.components.resultsPage.proctoringResultsViewTSX.proctoringMethods.${methodKey}`,
									),
								})}
							</div>
						</Box>
					</div>
					{Object.keys(optionCategories).length > 0 && (
						<div style={{ marginBottom: "40px", fontSize: "14px" }}>
							<Typography variant="h2" style={titleTextStyle}>
								{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.settingsSelected")}
							</Typography>
							<hr
								style={{
									lineHeight: "0.5px",
									width: "100%",
									backgroundColor: "#a6a6a6",
									margin: "6px 0px 24px",
								}}
							/>
							{Object.entries(optionCategories).map(([category, keys], i) => (
								<div
									key={i}
									style={{
										paddingBottom: "10px",
										fontSize: "14px",
									}}
								>
									<Typography
										variant="h3"
										style={{
											fontSize: "inherit",
											fontWeight: "700",
										}}
									>
										{category}
									</Typography>
									{keys.map((optionKey, index) => {
										const optionData = PROCTORING_OPTION_TEXT[optionKey];
										return (
											<div key={index} style={{ padding: "10px 6px", color: "#000" }}>
												<div
													style={{
														display: "flex",
														justifyContent: "space-between",
														maxWidth: "100%",
														padding: "4px 0",
														gap: "20px",
														lineHeight: "1",
														fontSize: "14px",
													}}
												>
													<div
														style={{
															fontWeight: "700",
															display: "flex",
															alignItems: "center",
															gap: "6px",
															width: "200px",
															maxWidth: "200px",
															height: "fit-content",
															justifyContent: "start",
															alignContent: "center",
														}}
													>
														<div
															style={{
																width: "30px",
																height: "30px",
																marginTop: "4px",
																marginBottom: "3px",
																justifyContent: "start",
																color: "#42296E",
															}}
														>
															{optionData.icon && <optionData.icon width={26} height={26} />}
														</div>
														<Typography
															variant="h4"
															style={{
																margin: 0,
																color: "inherit",
																fontWeight: "inherit",
																fontSize: "inherit",
																lineHeight: "inherit",
																display: "flex",
																justifyContent: "start",
																alignItems: "start",
																width: "165px",
																height: "fit-content",
															}}
														>
															{optionData.header}
														</Typography>
													</div>
													<div
														style={{
															display: "flex",
															justifyContent: "start",
															width: "100%",
															maxWidth: `calc(100% - 200px)`,
															minWidth: 0,
														}}
													>
														<div
															style={{
																display: "flex",
																flexDirection: "column",
																alignItems: "start",
																gap: "10px",
																maxWidth: "100%",
															}}
														>
															<div
																style={{
																	display: "flex",
																	flexBasis: "75%",
																	alignItems: "center",
																	width: "100%",
																	paddingTop: "5px",
																	color: "black",
																	fontWeight: 400,
																	lineHeight: "1.5",
																}}
															>
																{optionData.description}
															</div>
															{optionChildren.has(optionKey) && (
																<>
																	{optionChildren.get(optionKey)!.length > 0 && (
																		<>
																			{[
																				"tabWhiteList",
																				"tabBlockList",
																				"excludedWebTrafficRecordingUrls",
																				"recordInput",
																				"allowedExtensionsList",
																			].includes(optionKey) ? (
																				<CollapsibleList
																					items={
																						optionKey === "recordInput"
																							? (optionChildren.get(optionKey) || []).map((item, index) => (
																									<div key={index}>{item.text}</div>
																								))
																							: (optionChildren.get(optionKey) || [])?.map((item, index) => (
																									<div
																										key={index}
																										style={{
																											display: "flex",
																											flexDirection: "column",
																											alignItems: "start",
																											gap: "10px",
																											width: "100%",
																											maxWidth: "100%",
																											minWidth: 0,
																										}}
																									>
																										<div
																											style={{
																												display: "flex",
																												alignItems: "center",
																												overflow: "visible", // visible so focus outline can show
																												textOverflow: "ellipsis",
																												whiteSpace: "nowrap",
																												maxWidth: "100%",
																												minWidth: 0,
																											}}
																										>
																											<span style={{ minWidth: "20px" }}>{index + 1}.</span>
																											<EllipsisTooltip>
																												<Link
																													href={item.text}
																													target="_blank"
																													rel="noreferrer"
																													style={{ display: "inline-block", maxWidth: "635px" }}
																												>
																													{item.text}
																												</Link>
																											</EllipsisTooltip>
																										</div>
																									</div>
																								))
																					}
																				/>
																			) : optionKey === "applicationBlockMode" ? (
																				<>
																					<div style={{ fontWeight: 600 }}>
																						{optionChildren.get(optionKey)?.[0]?.text}
																					</div>
																					{optionChildren
																						.get(optionKey)!
																						.slice(1)
																						.map((value, index) => (
																							<CheckedSubOption key={`${optionKey}-${index}`}>
																								{value.text}
																							</CheckedSubOption>
																						))}
																					{optionKey === "applicationBlockMode" &&
																						optionChildren.has("applicationBlockList") && (
																							<CollapsibleApplicationList
																								allApplications={applications}
																								processNames={
																									optionChildren.get("applicationBlockList")?.map((i) => i.text) || []
																								}
																							/>
																						)}
																					{optionKey === "applicationBlockMode" &&
																						optionChildren.has("allowedApplicationsList") && (
																							<CollapsibleApplicationList
																								allApplications={applications}
																								processNames={
																									optionChildren.get("allowedApplicationsList")?.map((i) => i.text) ||
																									[]
																								}
																							/>
																						)}
																				</>
																			) : optionKey === "extensionBlockMode" ? (
																				<>
																					<div style={{ fontWeight: 600 }}>
																						{optionChildren.get(optionKey)?.[0]?.text}
																					</div>
																					{optionChildren
																						.get(optionKey)!
																						.slice(1)
																						.map((value, index) => (
																							<CheckedSubOption key={`${optionKey}-${index}`}>
																								{value.text}
																							</CheckedSubOption>
																						))}
																					{optionKey === "extensionBlockMode" &&
																						optionChildren.has("allowedExtensionsList") && (
																							<CollapsibleExtensionList
																								allExtensions={extensions}
																								customExtensions={
																									optionChildren
																										.get("allowedExtensionsList")
																										?.map((item) => item.text) || []
																								}
																							/>
																						)}
																				</>
																			) : (
																				renderNestedChildren(optionChildren.get(optionKey)!)
																			)}
																		</>
																	)}
																</>
															)}
														</div>
													</div>
												</div>
											</div>
										);
									})}
								</div>
							))}
						</div>
					)}
				</div>
			)}
		</Box>
	);
};

const getStartingSessionIndex = (proctoringSessions: SessionInfo[], initialProctoringSessionId?: string) => {
	const initialProctoringSessionIndex = initialProctoringSessionId
		? proctoringSessions.findIndex(({ uuid }) => uuid === initialProctoringSessionId)
		: -1;
	if (initialProctoringSessionIndex !== -1) {
		return initialProctoringSessionIndex;
	}
	const statusPriority = ["flagged", "unflagged", "incomplete", "unavailable", "pending", "error"];
	for (const status of statusPriority) {
		const statusSessionIndex = proctoringSessions.findIndex((session) => session.status === status);
		if (statusSessionIndex !== -1) {
			return statusSessionIndex;
		}
	}
	return 0;
};

export const VideoOverlay = ({
	useVideoEndedOverlay,
	isOverlayVisible,
	setPlay,
}: {
	useVideoEndedOverlay: boolean;
	isOverlayVisible: boolean;
	setPlay: () => void;
}) => {
	return (
		<button
			className={"video_overlay"}
			style={{
				opacity: isOverlayVisible ? 1 : 0,
				backgroundColor: useVideoEndedOverlay ? "#000000F2" : "#00000059",
			}}
			onClick={(e) => {
				setPlay();
				e.stopPropagation();
			}}
			aria-label={
				useVideoEndedOverlay
					? i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.restartVideoLabel")
					: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.playVideoLabel")
			}
		>
			<div
				className="overlay_button_circle"
				style={{ backgroundColor: useVideoEndedOverlay ? "#212121" : "#000000CC" }}
			>
				{useVideoEndedOverlay ? (
					<>
						<SvgIcon
							component={ReplayIcon}
							opacity={1}
							viewBox="0 0 130 130"
							style={{ width: "130", height: "130", color: "#F4F4F4", cursor: "pointer" }}
							className="overlay_icon"
						/>
						<SvgIcon
							component={ReplayIconGlowing}
							opacity={1}
							viewBox="0 0 130 130"
							style={{ width: "130", height: "130", color: "#F4F4F4", cursor: "pointer" }}
							className="overlay_icon_glowing"
						/>
					</>
				) : (
					<>
						<SvgIcon
							component={PlayOverlayIcon}
							opacity={1}
							viewBox="0 0 78 78"
							style={{ width: "78", height: "78", color: "#F4F4F4", cursor: "pointer" }}
							className="overlay_icon"
						/>
						<SvgIcon
							component={PlayOverlayIconGlowing}
							opacity={1}
							viewBox="0 0 78 78"
							style={{ width: "78", height: "78", color: "#F4F4F4", cursor: "pointer" }}
							className="overlay_icon_glowing"
						/>
					</>
				)}
			</div>
		</button>
	);
};

const SummaryCard = ({
	title,
	body,
	icon,
	titleColor,
	backgroundColor,
}: {
	title: string;
	body: string;
	icon: ReactNode;
	titleColor: string;
	backgroundColor: string;
}) => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			gap: "5px",
			width: "calc(100% - 10px)",
			height: "fit-content",
			minHeight: "fit-content",
			maxHeight: "fit-content",
			padding: "5px 12.5px 10px 12.5px",
			marginRight: "10px",
			backgroundColor: backgroundColor,
			borderRadius: "8px",
			boxSizing: "border-box",
		}}
	>
		<Box
			sx={{
				display: "flex",
				justifyContent: "start",
				alignItems: "center",
				gap: "5px",
				width: "100%",
				svg: {
					width: "24px",
					height: "24px",
				},
			}}
		>
			{icon}
			<Typography fontWeight="600" sx={{ color: titleColor }}>
				{title}
			</Typography>
		</Box>
		<Typography>{body}</Typography>
	</Box>
);

const FakeProgressBar = () => {
	const progress = useFakeProgress(false);
	return (
		<CoreProgressBar
			variant="determinate"
			value={progress * 100}
			height={8}
			color={"#7C3AED"}
			backgroundColor={"#E9D5FF"}
			width={"426px"}
			sx={{ maxWidth: "100%" }}
		/>
	);
};

const AiSummary = ({
	summaryData,
	closeSummary,
}: {
	summaryData: AiSummaryResponse | undefined;
	closeSummary: () => void;
}) => {
	const summaryDataPopulated = !!summaryData && Object.keys(summaryData).length > 0;
	const [borderHighlighted, setBorderHighlighted] = useState(!summaryDataPopulated);

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;

		if (summaryDataPopulated && borderHighlighted) {
			timeoutId = setTimeout(() => {
				setBorderHighlighted(false);
			}, 4000);
		}

		return () => clearTimeout(timeoutId);
	}, [summaryData, borderHighlighted]);

	const loadingComponent = (
		<Box
			role="status"
			aria-live="polite"
			sx={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
				gap: "40px",
				maxWidth: "100%",
			}}
		>
			<img src={circleLoadingGif} style={{ width: "180px", height: "135px" }} role="presentation" alt="" />
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: "5px",
					maxWidth: "100%",
					padding: "0px 10px",
					textAlign: "center",
				}}
			>
				<Typography style={{ color: "#6B6B6B" }}>
					{i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.aiSummary.analyzingProctoringData")}
				</Typography>
				<FakeProgressBar />
			</Box>
		</Box>
	);

	const summaryComponent = (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "start",
				alignItems: "center",
				gap: "20px",
				height: "fit-content",
				padding: "5px 10px 20px 20px",
				boxSizing: "border-box",
			}}
		>
			{summaryData?.summary && (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "5px",
					}}
				>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							width: "100%",
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: "start",
								alignItems: "center",
								gap: "5px",
								paddingTop: "10px",
							}}
						>
							<OverallSummaryIcon width="18px" height="18px" />
							<Typography fontWeight="600">
								{i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.aiSummary.overallSummary")}
							</Typography>
						</Box>
						<CoreIconButton variant="plain" onClick={closeSummary} style={{ color: "#6B6B6B" }}>
							<CloseXIcon />
						</CoreIconButton>
					</Box>
					<Typography style={{ marginRight: "10px" }}>{summaryData.summary}</Typography>
				</Box>
			)}
			{summaryData?.severe && (
				<SummaryCard
					title={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.aiSummary.severe")}
					body={summaryData.severe}
					icon={<SevereIcon />}
					titleColor="#C84A2B"
					backgroundColor="#FEF2F2"
				/>
			)}
			{summaryData?.major && (
				<SummaryCard
					title={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.aiSummary.major")}
					body={summaryData.major}
					icon={<MajorIcon />}
					titleColor="#B57025"
					backgroundColor="#B570250D"
				/>
			)}
			{summaryData?.minor && (
				<SummaryCard
					title={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.aiSummary.minor")}
					body={summaryData.minor}
					icon={<MinorIcon />}
					titleColor="#556C32"
					backgroundColor="#556C320D"
				/>
			)}
		</Box>
	);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				justifyContent: summaryDataPopulated ? "flex-start" : "center",
				alignItems: "center",
				gap: "20px",
				height: "475px",
				minHeight: "100%",
				border: borderHighlighted ? "4px solid transparent" : "1px solid #E4E4E4",
				background: borderHighlighted
					? `linear-gradient(#FFF, #FFF) padding-box, linear-gradient(96deg, #6B55FE -24.53%, #9350C7 24.99%, #ED7EF8 75.81%, #FFEC41 110.99%) border-box`
					: "#FFF",
				borderRadius: "10px",
				boxShadow: "0px 4px 6px -4px #0000001A, 0px 10px 15px -3px #0000001A",
				overflowY: "auto",
			}}
		>
			{summaryDataPopulated ? summaryComponent : loadingComponent}
		</Box>
	);
};

export const ProctoringResultView = (props: {
	// sessionUuid: string;
	courseId: number;
	// sessionNum: string | undefined | null;
	showVideo: boolean;
	/**
	 * defaults to false
	 */
	showTimestamps?: boolean;
	selectedLiveNotes?: LiveSessionNote[];
	// selectedProctoringSession?: SessionInfo | null;
	// totalSessions?: number | null;
	proctoringSessions: SessionInfo[];
	setProctoringSessions?: Dispatch<SetStateAction<SessionInfo[] | undefined>>;
	setShowStudentResults?: (value: boolean) => void;
	assessmentInfo: QuizInfoSummary | null;
	selectedStudent?: number;
	setSelectedStudent: (value: number | undefined) => void;
	studentList: { id: number; name: string; severityLevel?: number }[];
	initialProctoringSessionId?: string;
}) => {
	const showTimestamps = props.showTimestamps ?? false;
	const [duration, setDuration] = useState<number | undefined>(undefined);
	const [roomScanDuration, setRoomScanDuration] = useState<number | undefined>(undefined);
	const [firstEventDate, setFirstEventDate] = useState<Date | undefined>(undefined);
	const [timeOffset, setTimeOffset] = useState(0);
	const startingIndex = getStartingSessionIndex(props.proctoringSessions, props.initialProctoringSessionId);
	const [sessionUuid, setSessionUuid] = useState<string>(props.proctoringSessions[startingIndex].uuid);
	const [selectedProctoringSession, setSelectedProctoringSession] = useState<SessionInfo>(
		props.proctoringSessions[startingIndex],
	);
	const [totalSessions, setTotalSessions] = useState<number>(props.proctoringSessions.length);
	const [sessionNumber, setSessionNumber] = useState<number>(startingIndex + 1);

	const [isLoading, setIsLoading] = useState(true);

	const { portalContext, userPrivileges, instituteSettings } = useContext(PortalControllerContext);
	const [hasInstituteEditPermission, setHasInstituteEditPermission] = useState(false);
	const controlContainerRef = useRef<HTMLDivElement>(null);

	const fetchLinks = useCallback(() => {
		return apiClient.scratchNote.getLinks({
			proctoringSessionUuid: sessionUuid,
		});
	}, [sessionUuid]);

	const fetchLinksHook = useAsync(fetchLinks, [sessionUuid]);

	const fetchSignatureLink = useCallback(() => {
		return apiClient.proctoringSession.getPhotoUrl(sessionUuid, "signature");
	}, [sessionUuid]);

	const fetchSignatureHook = useAsync(fetchSignatureLink, [sessionUuid]);

	const [tab, setTab] = useState<Tab>("flaggedEvents");
	const [mainTabList, setMainTabList] = useState<TabType[]>();
	const [mainTab, setMainTab] = useState<MainTab>("recording");
	const { addTab, getTabIdByType } = useProctoringViewTabContext();
	const [mainTabOverflow, setMainTabOverflow] = useState<boolean>(false);

	const [_objectDetectionCapability, setObjectDetectionCapability] = useAtom(objectDetectionCapabilityAtom);
	const [_faceDetectionCapability, setFaceDetectionCapability] = useAtom(faceDetectionCapabilityAtom);

	// Register Tab IDs
	useEffect(() => {
		ALL_TAB_KEYS.forEach((key) => {
			addTab(key);
		});
	}, [addTab]);

	// Manually calculating overflow instead of setting scrollButtons = "auto" because "auto" always showed scroll buttons even if no overflow
	const handleMainTabsRef = useCallback(
		(el: HTMLDivElement | null) => {
			if (!el) return;

			const scroller = el.querySelector<HTMLElement>(".MuiTabs-scroller");
			const flexContainer = el.querySelector<HTMLElement>(".MuiTabs-flexContainer");

			if (!scroller || !flexContainer) {
				setMainTabOverflow(false);
				return;
			}

			// Overflow check function
			const checkOverflow = () => {
				setMainTabOverflow(flexContainer.scrollWidth > scroller.clientWidth);
			};

			// Initial check
			checkOverflow();

			// Setup resize observer + event
			const resizeObserver = new ResizeObserver(checkOverflow);
			resizeObserver.observe(flexContainer);
			resizeObserver.observe(scroller);
			window.addEventListener("resize", checkOverflow);

			// Cleanup when ref changes or unmounts
			return () => {
				window.removeEventListener("resize", checkOverflow);
				resizeObserver.disconnect();
			};
		},
		[setMainTabOverflow],
	);

	const userCanEditInstitute = canAccessResource(
		{ privileges: userPrivileges },
		{
			privilege: "INSTITUTE",
			action: "UPDATE",
			resourceScope: { instituteId: portalContext.instituteUuid, resourceId: portalContext.instituteUuid },
		},
	);
	useEffect(() => {
		const fetchPermissions = async () => {
			if (!userCanEditInstitute) {
				const perm = await apiClient.permission.doesUserHaveInstituteProfilePermissions();
				setHasInstituteEditPermission(perm);
			}
		};

		fetchPermissions().catch((error) => {
			console.error(error);
		});
	}, [userCanEditInstitute]);

	useEffect(() => {
		async function fetchApplicationData() {
			const APPLICATION_LIST = await api.getAllApplications();
			setApplications(APPLICATION_LIST);
		}

		async function fetchExtensionData() {
			const EXTENSION_LIST = await api.getAllExtensions();
			setExtensions(EXTENSION_LIST);
		}

		void fetchApplicationData();
		void fetchExtensionData();
	}, []);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			const updateSession = () => {
				const selectedSessionIndex = getStartingSessionIndex(
					props.proctoringSessions,
					props.initialProctoringSessionId,
				);
				setTotalSessions(props.proctoringSessions.length);
				setSessionNumber(selectedSessionIndex + 1);
				setSelectedProctoringSession(props.proctoringSessions[selectedSessionIndex]);
				setSessionUuid(props.proctoringSessions[selectedSessionIndex].uuid);
			};
			updateSession();
		}, 300); // 300ms debounce

		return () => clearTimeout(timeoutId);
	}, [props.proctoringSessions, props.initialProctoringSessionId]);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.has("sessionNum") && params.has("session")) {
			const sessionNum = Number(params.get("sessionNum"));
			if (sessionNum > 0 && sessionNum <= totalSessions) {
				setSessionUuid(props.proctoringSessions[sessionNum - 1].uuid);
				setSelectedProctoringSession(props.proctoringSessions[sessionNum - 1]);
				setTotalSessions(props.proctoringSessions.length);
				setSessionNumber(sessionNum);
			}
		}
	}, []);

	const [mediaGridHeight, setMediaGridHeight] = useState(0);

	useEffect(() => {
		const mediaGrid = document.getElementById("media_grid");

		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				if (entry.target === mediaGrid) {
					// + 70 because the timestampLinks section is positioned higher than the mediaGrid
					setMediaGridHeight(entry.contentRect.height + 70);
				}
			}
		});

		if (mediaGrid) {
			observer.observe(mediaGrid);
		}

		return () => {
			observer.disconnect();
		};
	}, []);

	// Player control variables
	const [controlOptions, setControlOptions] = useState({
		isPlaying: false,
		playbackSpeed: 1,
		volume: 0.5,
	});

	const [isRoomScanOverlayVisible, setIsRoomScanOverlayVisible] = useState(true);

	useEffect(() => {
		setControlOptions((controlOptions) => ({
			...controlOptions,
			volume: selectedProctoringSession.isWebcamAndScreenCaptureDeleted ? 0 : controlOptions.volume,
		}));
		setRoomScanControlOptions((roomScanControlOptions) => ({
			...roomScanControlOptions,
			volume: selectedProctoringSession.isRoomScanDeleted ? 0 : roomScanControlOptions.volume,
		}));
	}, [selectedProctoringSession]);

	const prevVolume = useRef<number>(controlOptions.volume);

	const [roomScanControlOptions, setRoomScanControlOptions] = useState({
		isPlaying: false,
		playbackSpeed: 1,
		volume: 0.5,
	});

	useEffect(() => {
		if (roomScanControlOptions.isPlaying) setIsRoomScanOverlayVisible(false);
		else setIsRoomScanOverlayVisible(true);
	}, [roomScanControlOptions]);

	const prevVolumeRoomScan = useRef<number>(roomScanControlOptions.volume);

	const [webCamHasError, setWebCamHasError] = useState(false);
	const [screenCaptureHasError, setScreenCaptureHasError] = useState(false);
	const [roomScanHasError, setRoomScanHasError] = useState(false);

	// Reducer function for useReducer React hook.
	// Handles 3 different dispatch cases
	const reducer = (state: VideoPlaybackState, action: VideoPlaybackAction) => {
		if (duration && (!webCamHasError || !screenCaptureHasError)) {
			switch (action.type) {
				case "play":
					if (state.timer < duration) {
						return {
							timer: state.timer + (performance.now() - state.lastTime) * action.speed,
							lastTime: performance.now(),
							skip: false,
						};
					} else {
						setControlOptions((controlOptions) => ({
							...controlOptions,
							isPlaying: !controlOptions.isPlaying,
						}));
						return { timer: state.timer, lastTime: performance.now(), skip: false };
					}
				case "pause":
					return { timer: state.timer, lastTime: performance.now(), skip: false };
				case "skip":
					return {
						timer: Math.max(timeOffset, Math.min(Math.round(action.time), duration)),
						lastTime: performance.now(),
						skip: true,
					};
			}
		}
		return state;
	};

	const roomScanReducer = (state: VideoPlaybackState, action: VideoPlaybackAction) => {
		if (roomScanDuration) {
			switch (action.type) {
				case "play":
					if (state.timer < roomScanDuration) {
						return {
							timer: state.timer + (performance.now() - state.lastTime) * action.speed,
							lastTime: performance.now(),
							skip: false,
						};
					} else {
						setRoomScanControlOptions((roomScanControlOptions) => ({
							...roomScanControlOptions,
							isPlaying: !roomScanControlOptions.isPlaying,
						}));
						return { timer: state.timer, lastTime: performance.now(), skip: false };
					}
				case "pause":
					return { timer: state.timer, lastTime: performance.now(), skip: false };
				case "skip":
					return {
						timer: Math.min(Math.round(action.time), roomScanDuration),
						lastTime: performance.now(),
						skip: true,
					};
			}
		}
		return state;
	};
	// Global timer variables
	const [playback, playbackDispatch] = useReducer(reducer, { timer: 0, lastTime: performance.now(), skip: false });
	const [roomScantimer, dispatchRoomScan] = useReducer(roomScanReducer, {
		timer: 0,
		lastTime: performance.now(),
		skip: false,
	});

	// Events
	const [startDate, setStartDate] = useState<Date>(new Date(0));
	const [events, setEvents] = useState<VideoEvent[]>([]);

	useEffect(() => {
		const isLoading = !(events?.length && events.length > 0);
		setIsLoading(isLoading);
	}, [events]);

	// For controlling when it is paused as opposed to played (code can probably be cleaned up)
	useEffect(() => {
		if (controlOptions.isPlaying) {
			if (playback.timer >= (duration ?? 0)) {
				playbackDispatch({ type: "skip", time: timeOffset });
			}
			const interval = setInterval(() => {
				playbackDispatch({ type: "play", speed: controlOptions.playbackSpeed });
			}, 1000 / 60);
			return () => clearInterval(interval);
		}
	}, [controlOptions.isPlaying, controlOptions.playbackSpeed]);

	useEffect(() => {
		if (roomScanControlOptions.isPlaying) {
			if (roomScantimer.timer >= (roomScanDuration ?? 0)) {
				dispatchRoomScan({ type: "skip", time: 0 });
			}
			const interval = setInterval(() => {
				dispatchRoomScan({ type: "play", speed: roomScanControlOptions.playbackSpeed });
			}, 1000 / 60);
			return () => clearInterval(interval);
		}
	}, [roomScanControlOptions.isPlaying, roomScanControlOptions.playbackSpeed]);

	// Setting the video player to play/pause (code can probably be used to clean up the useEffect previously)
	const setPlay = () => {
		// Used to reset the timer otherwise a paused video's time will continue to go up even when the video is paused (so unpausing it will cause a skip)
		// this will update the timer the second the video starts playing making sure that the calculations are correct (updating performance.now to the most recent time)
		playbackDispatch({ type: "pause" });
		setControlOptions((controlOptions) => ({
			...controlOptions,
			isPlaying: !controlOptions.isPlaying,
		}));
		const pauseImage = document.getElementById("pauseIconImage");
		setTimeout(() => {
			pauseImage?.setAttribute("style", "display: none");
		}, 250);
	};

	const setRoomScanPlay = () => {
		// Used to reset the timer otherwise a paused video's time will continue to go up even when the video is paused (so unpausing it will cause a skip)
		// this will update the timer the second the video starts playing making sure that the calculations are correct (updating performance.now to the most recent time)
		dispatchRoomScan({ type: "pause" });
		setRoomScanControlOptions((roomScanControlOptions) => ({
			...roomScanControlOptions,
			isPlaying: !roomScanControlOptions.isPlaying,
		}));
	};

	// Helps set/handle the volume changes of the video player
	const setVolume: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		prevVolume.current = Number(e.target.value);
		setControlOptions((controlOptions) => ({
			...controlOptions,
			volume: Number(e.target.value),
		}));
	};

	const toggleVolume = () => {
		setControlOptions((prevState) => ({
			...prevState,
			volume: controlOptions.volume > 0 ? 0 : prevVolume.current,
		}));
	};

	const setRoomScanVolume: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		prevVolumeRoomScan.current = Number(e.target.value);
		setRoomScanControlOptions((roomScanControlOptions) => ({
			...roomScanControlOptions,
			volume: Number(e.target.value),
		}));
	};

	const toggleVolumeRoomScan = () => {
		setRoomScanControlOptions((prevState) => ({
			...prevState,
			volume: roomScanControlOptions.volume > 0 ? 0 : prevVolumeRoomScan.current,
		}));
	};

	// Helps set/handle the playback speed changes of the video player
	const setSpeed = (value: string) => {
		const num = parseFloat(value);
		setControlOptions((controlOptions) => ({
			...controlOptions,
			playbackSpeed: num,
		}));
	};

	const setRoomScanSpeed = (value: string) => {
		const num = parseFloat(value);
		if (showRoomScanVideoSpeed) {
			setShowRoomScanVideoSpeed(false);
		}
		setRoomScanControlOptions((roomScanControlOptions) => ({
			...roomScanControlOptions,
			playbackSpeed: num,
		}));
	};

	const [showRoomScanVideoSpeed, setShowRoomScanVideoSpeed] = useState(false);
	const [showFullscreenButtons, setShowFullscreenButtons] = useState(false);
	const [sideBarNotesNewInput, setSideBarNotesNewInput] = useState("");
	const [sideBarRoomScanNotesNewInput, setSideBarRoomScanNotesNewInput] = useState("");
	const [rawSessionNotes, setRawSessionNotes] = useState<
		{
			id: number;
			content: string;
			timestamp: string;
		}[]
	>([]);
	const [sessionNotes, setSessionNotes] = useState<AutomatedProctoringSessionNote[]>([]);
	const [rawRoomScanNotes, setRawRoomScanNotes] = useState<
		{
			id: number;
			content: string;
			timestamp: number;
		}[]
	>([]);
	const [roomScanNotes, setRoomScanNotes] = useState<AutomatedProctoringSessionNote[]>([]);
	const [waitingAddNote, setWaitingAddNote] = useState(false);

	const getSessionNotesHandler = () => {
		getSessionNotes(sessionUuid, props.courseId)
			.then((resp) => (resp ? setRawSessionNotes(resp) : null))
			.catch((e) => {
				coreToast.error(i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.failGetNote"));
				console.error(e);
			});
	};

	const addNoteHandler = async (content: string) => {
		if (!firstEventDate) {
			return false;
		}
		const timestamp = new Date(firstEventDate.getTime() + playback.timer - timeOffset);
		return await addSessionNote(sessionUuid, props.courseId, content, timestamp).then(() => {
			getSessionNotesHandler();
			return true;
		});
	};

	const editNoteHandler = async (noteId: number, content: string) => {
		return await editSessionNote(sessionUuid, props.courseId, noteId, content).then(() => {
			getSessionNotesHandler();
			return true;
		});
	};

	const deleteNoteHandler = async (noteId: number) => {
		return await deleteSessionNote(sessionUuid, props.courseId, noteId).then(() => {
			getSessionNotesHandler();
			return true;
		});
	};

	const getRoomScanNotesHandler = () => {
		getRoomScanNotes(sessionUuid, props.courseId)
			.then((resp) => (resp ? setRawRoomScanNotes(resp) : null))
			.catch((e) => {
				coreToast.error(i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.failGetNote"));
				console.error(e);
			});
	};

	const addRoomScanNoteHandler = async (content: string) => {
		const currTime = roomScantimer.timer / 1000;
		return await addRoomScanNote(sessionUuid, props.courseId, content, currTime).then(() => {
			getRoomScanNotesHandler();
			return true;
		});
	};

	const editRoomScanNoteHandler = async (noteId: number, content: string) => {
		return await editRoomScanNote(sessionUuid, props.courseId, noteId, content).then(() => {
			getRoomScanNotesHandler();
			return true;
		});
	};

	const deleteRoomScanNoteHandler = async (noteId: number) => {
		return await deleteRoomScanNote(sessionUuid, props.courseId, noteId).then(() => {
			getRoomScanNotesHandler();
			return true;
		});
	};

	useEffect(() => {
		if (firstEventDate) {
			setSessionNotes(
				rawSessionNotes
					.map((x) => ({
						id: x.id,
						content: x.content,
						time: (new Date(x.timestamp).getTime() - firstEventDate.getTime() + timeOffset) / 1000,
						date: new Date(x.timestamp),
					}))
					.sort((a, b) => a.time - b.time),
			);
		}
	}, [firstEventDate, rawSessionNotes, timeOffset]);

	useEffect(() => {
		setRoomScanNotes(
			rawRoomScanNotes
				.map((x) => ({
					id: x.id,
					content: x.content,
					time: x.timestamp,
					date: new Date(x.timestamp),
				}))
				.sort((a, b) => a.time - b.time),
		);
	}, [rawRoomScanNotes]);

	const [screenVidSources, setScreenVidSources] = useState<{ [key: string]: string }>({});
	const [webcamVidSources, setWebcamVidSources] = useState<{ [key: string]: string }>({});
	const [roomScanVidSource, setRoomScanVidSource] = useState<{ [key: string]: string }>({});
	// const [showingVideo, setShowingVideo] = useState(props.showVideo);
	const [webcamVideoOptions, setWebcamVideoOptions] = useState<VideoSettings | null>();
	const [screencaptureVideoOptions, setScreencaptureVideoOptions] = useState<VideoSettings | null>();
	const [roomScanVideoOptions, setRoomScanVideoOptions] = useState<VideoSettings | null>();
	const [videoOptionsFrame, setVideoOptionsFrame] = useState<VideoSettings | null>();
	const { screenTimestampsArr, webcamTimestampsArr, setTimestampEvents } = useTimestamps({ videoEvents: events });
	const roomScanTimestampsArr: TimestampsArr = [{ vid: "placeholder" }, { vid: 0, start: 0 }];
	const [visibleLiveNotes, setVisibleLiveNotes] = useState<LiveProctoringSessionNote[]>([]);
	const [showAudioThumbnail, setShowAudioThumbnail] = useState(false);
	const [shouldHideCamVid, setShouldHideCamVid] = useState(false);
	const [showVideoUnavailable, setShowVideoUnavailable] = useState(false);
	const [proctoringOptions, setProctoringOptions] = useState<Partial<ProctoringAssessmentConfigurationV1>>();
	const [renderVideo, setRenderVideo] = useState<boolean>();
	const [videoEvents, setVideoEvents] = useState<VideoEvent[]>([]);
	const [applications, setApplications] = useState<Application[]>([]);
	const [extensions, setExtensions] = useState<Extension[]>([]);
	const [areVideosFetched, setAreVideosFetched] = useState(false);
	const [aiSummary, setAiSummary] = useState<AiSummaryResponse | undefined>();
	const [aiSummaryLoading, setAiSummaryLoading] = useState(true);
	const [aiSummaryShowing, setAiSummaryShowing] = useState(false);
	const hasTypedSignature = useMemo(() => videoEvents.some((ev) => ev.name === "typed signature"), [videoEvents]);

	// Standardized video options
	const getData = async () => {
		setRenderVideo(false);
		setMainTabList(undefined);
		setIsLoading(true);
		setAreVideosFetched(false);
		// Events

		const allLoggedEvents = await getLoggedEvents(sessionUuid, props.courseId);
		const startDate = findStartDate(allLoggedEvents);
		setStartDate(startDate);
		const videoEvents = convertLoggingEventsToVideoEvents({ loggingEvents: allLoggedEvents, startDate });
		if (!videoEvents) {
			// TODO: handle something going wrong
			console.error("No video events");
			return;
		}
		setVideoEvents(videoEvents);

		const startProctoringEvent = videoEvents.find((e) => {
			return e && e.type === "session" && e.name === "start proctoring" && e.details?.success;
		});

		if (
			startProctoringEvent?.details?.options?.videoRecordingMode === "off" &&
			startProctoringEvent?.details?.options?.audioRecordingMode != "off"
		) {
			setShowAudioThumbnail(true);
		} else {
			setShowAudioThumbnail(false);
		}

		if (
			startProctoringEvent?.details?.options?.videoRecordingMode === "off" &&
			startProctoringEvent?.details?.options?.audioRecordingMode != "off" &&
			startProctoringEvent?.details?.options?.recordScreen
		) {
			setShouldHideCamVid(true);
		}
		if (startProctoringEvent?.details?.options) {
			setProctoringOptions(startProctoringEvent?.details?.options as ProctoringAssessmentConfigurationV1);
		} else {
			setProctoringOptions({});
		}
		try {
			getSessionNotesHandler();
			getRoomScanNotesHandler();
		} catch (e) {
			console.error("Error getting notes", e);
		}

		const videoJsOptions = {
			controls: true,
			responsive: true,
			aspectRatio: "16:9",
			fluid: true,
			userActions: {
				click: false,
			},
			controlBar: {
				playToggle: false,
				volumePanel: false,
				progressControl: false,
				remainingTimeDisplay: false,
				pictureInPictureToggle: false,
				fullscreenToggle: false,
			},
			bigPlayButton: false,
		};

		const videoJsFrameOptions = {
			controls: false,
			responsive: false,
			aspectRatio: "16:9",
			fluid: true,
			userActions: {
				click: false,
			},
			controlBar: {
				playToggle: false,
				volumePanel: false,
				progressControl: false,
				remainingTimeDisplay: false,
				pictureInPictureToggle: false,
				fullscreenToggle: false,
			},
			bigPlayButton: false,
		};

		setWebcamVideoOptions(videoJsOptions);
		setScreencaptureVideoOptions(videoJsOptions);
		setRoomScanVideoOptions(videoJsOptions);
		setVideoOptionsFrame(videoJsFrameOptions);
		// List of video sources and there status (available or processing)
		const [screencapturePromise, webcamPromise, roomScanPromise] = await Promise.allSettled([
			getVideoUrl(sessionUuid, "screencapture", props.courseId),
			getVideoUrl(sessionUuid, "webcam", props.courseId),
			getRoomScanVideoUrl(sessionUuid),
		]);
		let screenAvail = false;
		let webcamAvail = false;
		if (screencapturePromise.status === "fulfilled") {
			screencapturePromise.value ? setScreenVidSources(screencapturePromise.value) : setScreenVidSources({});
			screenAvail = screencapturePromise.value !== null ? Object.keys(screencapturePromise.value).length > 0 : false;
		}

		if (webcamPromise.status === "fulfilled") {
			webcamPromise.value ? setWebcamVidSources(webcamPromise.value) : setWebcamVidSources({});
			webcamAvail = webcamPromise.value !== null ? Object.keys(webcamPromise.value).length > 0 : false;
		}

		if (!screenAvail && !webcamAvail) {
			setShowVideoUnavailable(true);
		} else {
			setShowVideoUnavailable(false);
		}

		if (roomScanPromise.status === "fulfilled" && roomScanPromise.value) {
			setRoomScanVidSource(roomScanPromise.value);
		} else {
			setRoomScanVidSource({});
		}
		setRenderVideo(true);
		setAreVideosFetched(true);
	};

	const getAiSummaryData = async () => {
		setAiSummaryLoading(true);

		try {
			const summary = await apiClient.proctoringSession.getAiSummary(sessionUuid);
			setAiSummary(summary);
		} catch (error) {
			console.error("Failed to fetch AI Summary", error);
		} finally {
			setAiSummaryLoading(false);
		}
	};

	const updateEvents = async () => {
		const allLoggedEvents = await getLoggedEvents(sessionUuid, props.courseId);
		const videoEvents = convertLoggingEventsToVideoEvents({ loggingEvents: allLoggedEvents, startDate });
		if (!videoEvents) {
			// TODO: handle something going wrong
			console.error("No video events");
			return;
		}
		setVideoEvents(videoEvents);
	};

	useEffect(() => {
		setWebcamVideoOptions(null);
		setScreencaptureVideoOptions(null);
		setRoomScanVideoOptions(null);
		setAiSummary(undefined);
		setAiSummaryShowing(false);

		setWebCamHasError(false);
		setScreenCaptureHasError(false);
		setRoomScanHasError(false);

		setRoomScanControlOptions((roomScanControlOptions) => ({
			...roomScanControlOptions,
			isPlaying: false,
			playbackSpeed: 1,
		}));
		getAiSummaryData().catch(console.error);
		getData().catch(console.error);

		playbackDispatch({ type: "pause" });
		setControlOptions((controlOptions) => ({
			...controlOptions,
			isPlaying: false,
		}));
		playbackDispatch({ type: "skip", time: 0 });

		dispatchRoomScan({ type: "pause" });
		dispatchRoomScan({ type: "skip", time: 0 });
		setRoomScanControlOptions((roomScanControlOptions) => ({
			...roomScanControlOptions,
			isPlaying: false,
		}));
	}, [sessionUuid]);

	useEffect(() => {
		const filterEvents = (events: VideoEvent[]) => {
			// If there exists a gap between events of more than 4 hours, trim the events to the one right before the gap
			const FOUR_HOURS_IN_SECONDS = 4 * 60 * 60;
			let trimIndex = events.length;

			// Find the first gap of more than 4 hours
			for (let i = 0; i < events.length - 1; i++) {
				const currentEvent = events[i];
				const nextEvent = events[i + 1];
				const gap = nextEvent.time - currentEvent.time;
				if (gap > FOUR_HOURS_IN_SECONDS) {
					trimIndex = i + 1;
					break;
				}
			}

			// Trim events to just before the gap
			const trimmedEvents = events.slice(0, trimIndex);

			// Find the latest time among recording end and quiz end events
			const latestEndTime = trimmedEvents.reduce((latestTime, event) => {
				if (event.type === "session") {
					if (
						event.name === "end proctoring" ||
						event.name === "end quiz" ||
						event.name === "end recording" ||
						event.name === "force stop proctoring" ||
						event.name === "no submission"
					) {
						return Math.max(latestTime, event.time);
					}
				}
				return latestTime;
			}, 0);

			// If we have neither, return the trimmed events
			if (latestEndTime === 0) {
				return trimmedEvents;
			}

			// Filter out events that occur after the latest end time
			return trimmedEvents.filter(
				(event) => event.time <= latestEndTime || (event.type === "session" && event.name === "upload incomplete"),
			);
		};

		const updateEvents = () => {
			const videoEventTimes = videoEvents.map((x) => new Date(x.logged_time).getTime());
			const startTime = Math.min(...videoEventTimes);
			const endTime = Math.max(...videoEventTimes);
			const filteredLiveNotes = props.selectedLiveNotes?.filter(
				(x) =>
					videoEvents.length > 0 &&
					x.createdAt >= new Date(startTime) &&
					x.createdAt <= new Date(endTime) &&
					(x.edits?.length ?? 0) > 0,
			);
			const noteEvents: VideoEvent[] =
				filteredLiveNotes?.map((x) => {
					const time = (x.createdAt.getTime() - new Date(videoEvents[0].logged_time).getTime()) / 1000;

					return {
						key: x.id,
						time: time,
						name: "create",
						type: "live_session_note",
						details: {
							editorName: x.edits[0].editorName ?? null,
							noteContent: x.edits[x.edits.length - 1].content,
						},
						flag_level: 0,
						resolved: false,
						logged_time: x.createdAt.toISOString(),
					};
				}) ?? [];

			const sortedEvents = [...videoEvents, ...noteEvents].sort((a, b) => a.time - b.time);
			const allEvents = filterEvents(sortedEvents);
			const duration = ((allEvents[allEvents.length - 1]?.time ?? 0) - (allEvents[0]?.time ?? 0)) * 1000;
			const addedDurationMetadataEvents = allEvents
				.map((x) => {
					if (x.type === "machine-learning" && x.name !== "flagged-keyword") {
						const machineLearningEvent = x as unknown as
							| ZodToReadable<typeof ObjectDetectionEventSchema>
							| ZodToReadable<typeof FaceDetectionEventSchema>;
						const nextMatchingEvent = allEvents.find((e) => {
							if (e.type !== "machine-learning") return false;
							const event = e as unknown as
								| ZodToReadable<typeof ObjectDetectionEventSchema>
								| ZodToReadable<typeof FaceDetectionEventSchema>;
							return event.name === machineLearningEvent.name && event.time > machineLearningEvent.time;
						}) as unknown as
							| ((ZodToReadable<typeof ObjectDetectionEventSchema> | ZodToReadable<typeof FaceDetectionEventSchema>) & {
									ids?: number[];
							  })
							| undefined;
						if (!nextMatchingEvent) return x;

						const duration = Number(nextMatchingEvent.time) - Number(machineLearningEvent.time);
						return {
							...x,
							details: {
								...x.details,
								metadata: { duration: duration * 1000 },
							},
						};
					} else {
						return x;
					}
				})
				.filter((e): e is VideoEvent => e !== null)
				.filter((e) => {
					if (e.type !== "machine-learning") return true;
					const event = e as unknown as
						| ZodToReadable<typeof ObjectDetectionEventSchema>
						| ZodToReadable<typeof FaceDetectionEventSchema>
						| ZodToReadable<typeof FlaggedKeywordEventSchema>;
					return (
						(event.name === "object-detection" && event.details.currentState !== "detected-no-object") ||
						(event.name === "face-detection" && event.details.currentState !== "detected-target-face") ||
						event.name === "flagged-keyword"
					);
				});

			setEvents(addedDurationMetadataEvents);
			setTimestampEvents(addedDurationMetadataEvents);
			setDuration(duration);
			setFirstEventDate(new Date(allEvents[0]?.logged_time));
			setVisibleLiveNotes(
				filteredLiveNotes?.map((x) => ({
					id: x.id,
					time: (x.createdAt.getTime() - new Date(videoEvents[0]?.logged_time).getTime()) / 1000,
					content: x.edits?.[x.edits.length - 1]?.content,
					date: x.createdAt,
				})) ?? [],
			);
		};
		updateEvents();
	}, [props.selectedLiveNotes, videoEvents]);

	useEffect(() => {
		void loadRoomScanThumbnails(roomScanVidSource);
	}, [roomScanDuration]);

	// Used to calculate the amount of shading needed for the custom slider bar
	const colorAmount = ((playback.timer - timeOffset) / ((duration ?? 0) - timeOffset)) * 100;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
	(document?.querySelector(":root") as HTMLElement)?.style?.setProperty(
		"--percentage",
		`${(colorAmount / 100 - 0.5) * 18}px`,
	);

	const shouldShowDownload =
		areVideosFetched &&
		((Object.keys(webcamVidSources).length > 0 &&
			!Object.values(webcamVidSources).some((source) => source === "processing")) ||
			(Object.keys(screenVidSources).length > 0 &&
				!Object.values(screenVidSources).some((source) => source === "processing")));

	const [downloadRequestStatus, setDownloadRequestStatus] = useState<DownloadRequestStatusType>("unsent");

	document.addEventListener("click", function handleClickOutsideBox(event) {
		if (showFullscreenButtons) {
			const button = document.getElementById("fullscreenModeButton");
			if (button?.contains(event.target as Node)) {
				return;
			} else {
				setShowFullscreenButtons(false);
			}
		}
	});

	const [inFullscreen, setInFullscreen] = useState(false);
	const [createdObserver, setCreatedObserver] = useState(false);
	const [stringExtend, setStringExtend] = useState("_fs");

	const setFullscreen = useCallback(() => {
		// The page is not in an iframe
		if (!document.fullscreenElement) {
			void document.getElementById("entire_player")?.requestFullscreen();
			if (!createdObserver) {
				setCreatedObserver(true);
				makeObserver();
			}
		} else {
			void document.exitFullscreen();
		}
	}, [createdObserver]);

	useEffect(() => {
		const handleFullscreenChange = () => {
			setInFullscreen(Boolean(document.fullscreenElement));
		};

		document.addEventListener("fullscreenchange", handleFullscreenChange);

		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
		};
	}, []);

	// Makes an observer to check when the videos are in fullscreen or not.
	function makeObserver() {
		// Select the node that will be observed for mutations
		const targetNode = document.getElementById("entire_player");

		// Options for the observer (which mutations to observe)
		const config = { attributes: true, childList: true, subtree: true };

		// Callback function to execute when mutations are observed
		const callback = (mutationList: { type: string }[], _observer: MutationObserver) => {
			for (const mutation of mutationList) {
				if (mutation.type === "attributes") {
					if (document.fullscreenElement) {
						setInFullscreen(true);
					} else {
						setInFullscreen(false);
					}
				}
			}
		};

		// Create an observer instance linked to the callback function
		const observer = new MutationObserver(callback);

		// Start observing the target node for configured mutations
		if (targetNode) {
			observer.observe(targetNode, config);
		}
		// Later, you can stop observing
		// observer.disconnect();
	}

	function modifyFSMode(changedMode: string) {
		document.querySelectorAll("#fullscreenMenuHolder .hide").forEach((e) => {
			e.classList.remove("hide");
		});
		switch (changedMode) {
			case "_fs":
				document.getElementById("Centered_fs")?.classList.add("hide");
				break;
			case "_fs_lm":
				document.getElementById("Left_and_Minimized_fs")?.classList.add("hide");
				break;
			case "_fs_rm":
				document.getElementById("Right_and_Minimized_fs")?.classList.add("hide");
				break;
			case "_fs_l":
				document.getElementById("Left_fs")?.classList.add("hide");
				break;
			case "_fs_r":
				document.getElementById("Right_fs")?.classList.add("hide");
				break;
		}
		setStringExtend(changedMode);
	}

	const [showEmailModal, setShowEmailModal] = useState<boolean>(false);

	const handleFullscreenKey = (setFullscreen: () => void) => {
		const focusedElement = document.activeElement;
		if (!focusedElement?.closest(".MuiModal-root")) {
			setFullscreen();
		}
	};

	const addKeyBoardHandler = ({
		event,
		setFullscreen,
		playback,
		playbackDispatch,
		showEmailModal,
		setPlay,
		setControlOptions,
		isErrorCase,
	}: {
		event: KeyboardEvent;
		setFullscreen: () => void;
		playback: VideoPlaybackState;
		playbackDispatch: (value: VideoPlaybackAction) => void;
		showEmailModal: boolean;
		setPlay: () => void;
		setControlOptions: (value: { isPlaying: boolean; playbackSpeed: number; volume: number }) => void;
		isErrorCase: boolean;
	}) => {
		if (showEmailModal) {
			return;
		}
		// if recording(s) has an error, only allow fullscreen shortcut
		if (isErrorCase) {
			if (event.key === "f" || event.key === "F") {
				handleFullscreenKey(setFullscreen);
			}
			return;
		}

		if (event.key === " ") {
			const el = document.activeElement as HTMLElement | null;
			const tag = el?.tagName?.toLowerCase();
			const role = el?.getAttribute("role")?.toLowerCase();

			// Known interactive tags and ARIA roles
			const interactiveTags = ["button", "input", "select", "textarea", "a"];
			const interactiveRoles = [
				"button",
				"switch",
				"checkbox",
				"radio",
				"combobox",
				"menuitem",
				"option",
				"listbox",
				"tab",
				"slider",
				"option",
			];

			const isFocusable = el && el !== document.body;
			const isInteractiveTag = tag && interactiveTags.includes(tag);
			const isInteractiveRole = role && interactiveRoles.includes(role);

			if (!isFocusable || (!isInteractiveTag && !isInteractiveRole)) {
				event.preventDefault();
				setPlay();
			}
		} else if (event.key === "f" || event.key === "F") {
			handleFullscreenKey(setFullscreen);
		} else if (event.key === "ArrowRight") {
			const focusedElement = document.activeElement;
			if (
				(focusedElement?.tagName.toLowerCase() !== "input" &&
					focusedElement?.role !== "tab" &&
					focusedElement?.id !== "volume_button") ||
				focusedElement?.id === "seekbar_Video"
			) {
				playbackDispatch({ type: "skip", time: playback.timer + 1000 });
			}
		} else if (event.key === "ArrowLeft") {
			const focusedElement = document.activeElement;
			if (
				(focusedElement?.tagName.toLowerCase() !== "input" &&
					focusedElement?.role !== "tab" &&
					focusedElement?.id !== "volume_button") ||
				focusedElement?.id === "seekbar_Video"
			) {
				playbackDispatch({ type: "skip", time: playback.timer - 1000 });
			}
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			setControlOptions({ ...controlOptions, volume: Math.min(controlOptions.volume + 0.1, 1) });
		} else if (event.key === "ArrowDown") {
			event.preventDefault();
			setControlOptions({ ...controlOptions, volume: Math.max(controlOptions.volume - 0.1, 0) });
		} else if (event.key === "m" || event.key === "M") {
			toggleVolume();
		}
	};

	useEffect(() => {
		const handleKeyboard = (event: KeyboardEvent) => {
			mainTab === "roomScan"
				? addKeyBoardHandler({
						event,
						setFullscreen,
						playback: roomScantimer,
						playbackDispatch: dispatchRoomScan,
						showEmailModal,
						setPlay: setRoomScanPlay,
						setControlOptions: setRoomScanControlOptions,
						isErrorCase: roomScanHasError,
					})
				: addKeyBoardHandler({
						event,
						setFullscreen,
						playback,
						playbackDispatch,
						showEmailModal,
						setPlay,
						setControlOptions,
						isErrorCase: webCamHasError && screenCaptureHasError,
					});
		};

		document.addEventListener("keydown", handleKeyboard);
		return () => document.removeEventListener("keydown", handleKeyboard);
	}, [
		playback,
		roomScantimer,
		showEmailModal,
		mainTab,
		webCamHasError,
		screenCaptureHasError,
		roomScanHasError,
		setFullscreen,
	]);

	const controlIconSx = {
		color: "white",
		fontSize: inFullscreen ? "32px" : "22px",
		stroke: "white",
		fill: "none",
	};

	// load thumbnails for notes
	// one thumbnail every two minutes, min 24, max 60
	const numberOfNoteThumbnails = Math.min(
		Math.max(24, !duration ? 0 : Math.round((duration - timeOffset) / (2 * 60 * 1000))),
		60,
	);
	const [noteThumbnails, setNoteThumbnails] = useState(
		new Array<{ startTime?: number; endTime?: number; canvas?: HTMLCanvasElement }>(numberOfNoteThumbnails),
	);

	const [roomScanNoteThumbnails, setRoomScanNoteThumbnails] = useState(
		new Array<{ startTime?: number; endTime?: number; canvas?: HTMLCanvasElement }>(24),
	);

	const waitForVideoLoad = (videoEl: HTMLVideoElement) => {
		return new Promise((resolve) => {
			videoEl.addEventListener("load", function loadListener() {
				videoEl.removeEventListener("load", loadListener);
				resolve(true);
			});
			setTimeout(() => resolve(false), 3000);
		});
	};
	const loadVideoAtTime = (videoEl: HTMLVideoElement, time: number) => {
		return new Promise((resolve, reject) => {
			videoEl.addEventListener("canplay", function canPlayListener() {
				videoEl.removeEventListener("canplay", canPlayListener);
				resolve(true);
			});
			videoEl.addEventListener("error", function errorListener() {
				videoEl.removeEventListener("error", errorListener);
				reject();
			});
			setTimeout(() => resolve(false), 3000);

			videoEl.currentTime = time;
		});
	};
	const loadThumbnails = async (videoSources: Record<string, string>) => {
		let currentSource: string = String(Object.keys(videoSources)[0]);

		const hiddenVideo = document.createElement("video");
		hiddenVideo.width = 96;
		hiddenVideo.height = 72;
		hiddenVideo.src = `${videoSources?.[currentSource]}`;

		await waitForVideoLoad(hiddenVideo);

		if (duration) {
			const thumbnailsCopy = [...noteThumbnails];
			const timeFrag = (duration - timeOffset) / numberOfNoteThumbnails;
			for (const [index, _thumbnail] of thumbnailsCopy.entries()) {
				const time = (timeFrag * index + timeOffset) / 1000;
				const canvas = document.createElement("canvas");
				canvas.width = hiddenVideo.videoWidth;
				canvas.height = hiddenVideo.videoHeight;
				const context = canvas.getContext("2d");

				// get webcam video for given time
				const webcamTimestamp = webcamTimestampsArr[binSearch(webcamTimestampsArr, time)];
				if (currentSource !== String(webcamTimestamp.vid)) {
					// update hidden video source to current webcam video
					currentSource = String(webcamTimestamp.vid);
					hiddenVideo.src = `${videoSources?.[currentSource]}`;
				}
				await loadVideoAtTime(hiddenVideo, Math.max(0.1, time - (webcamTimestamp?.start ?? 0)))
					.then(() => {
						if (context && hiddenVideo.videoWidth > 0 && hiddenVideo.videoHeight > 0) {
							context.drawImage(hiddenVideo, 0, 0, hiddenVideo.videoWidth, hiddenVideo.videoHeight);
						}
					})
					.catch(() => {
						console.warn("Thumbnail video could not load.");
					});
				thumbnailsCopy[index] = {
					startTime: time,
					endTime: time + timeFrag / 1000,
					canvas: canvas,
				};
			}
			setNoteThumbnails(thumbnailsCopy);
		}
	};

	useEffect(() => {
		if (sessionNotes.length > 0 || visibleLiveNotes.length > 0) {
			void loadThumbnails(webcamVidSources);
		}
	}, [webcamVidSources, sessionNotes, visibleLiveNotes]);

	const loadRoomScanThumbnails = async (videoSources: Record<string, string>) => {
		let currentSource = 0;

		const hiddenVideo = document.createElement("video");
		hiddenVideo.width = 96;
		hiddenVideo.height = 72;
		hiddenVideo.src = `${videoSources?.[currentSource]}`;

		await waitForVideoLoad(hiddenVideo);

		if (roomScanDuration) {
			const thumbnailsCopy = [...roomScanNoteThumbnails];
			const timeFrag = roomScanDuration / 24;
			for (const [index, _thumbnail] of thumbnailsCopy.entries()) {
				const time = (timeFrag * index) / 1000;
				const canvas = document.createElement("canvas");
				canvas.width = hiddenVideo.videoWidth;
				canvas.height = hiddenVideo.videoHeight;
				const context = canvas.getContext("2d");

				// get webcam video for given time
				const webcamTimestamp = roomScanTimestampsArr[binSearch(roomScanTimestampsArr, time)];
				if (currentSource < webcamTimestamp.vid) {
					// update hidden video source to current webcam video
					currentSource = webcamTimestamp.vid;
					hiddenVideo.src = `${videoSources?.[currentSource]}`;
				}
				await loadVideoAtTime(hiddenVideo, Math.max(0.1, time - (webcamTimestamp?.start ?? 0)))
					.then(() => {
						if (context && hiddenVideo.videoWidth > 0 && hiddenVideo.videoHeight > 0) {
							context.drawImage(hiddenVideo, 0, 0, hiddenVideo.videoWidth, hiddenVideo.videoHeight);
						}
					})
					.catch(() => {
						console.warn("Thumbnail video could not load.");
					});
				thumbnailsCopy[index] = {
					startTime: time,
					endTime: time + timeFrag / 1000,
					canvas: canvas,
				};
			}
			setRoomScanNoteThumbnails(thumbnailsCopy);
		}
	};

	function handleImgError(ev: React.SyntheticEvent<HTMLImageElement, Event>) {
		if (ev.target instanceof HTMLImageElement && ev.target.src !== substitutePhoto) {
			ev.target.src = substitutePhoto;
		}
	}

	const [userEnteredEmail, setUserEnteredEmail] = useState("");
	const [userEnteredName, setUserEnteredName] = useState("");

	const rightTabList = useMemo(() => {
		const tabs: TabType[] = [
			{
				key: "flaggedEvents",
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.flaggedEvents"),
				icon: <FlaggedEventsIcon />,
				id: getTabIdByType("flaggedEvents"),
			},
		];
		if (sessionNotes.length > 0) {
			tabs.push({
				key: "notes",
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.notes"),
				icon: <NotesIcon />,
				id: getTabIdByType("notes"),
			});
		}
		if (visibleLiveNotes.length > 0) {
			tabs.push({
				key: "liveNotes",
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.liveNote"),
				icon: <LiveNotesIcon />,
				// No Id needed since it is the only "tab" on screen -> so it is not a "tab"
			});
		}
		return tabs;
	}, [sessionNotes, visibleLiveNotes]);

	useEffect(() => {
		if (proctoringOptions) {
			const hasWebcamRecording = proctoringOptions.videoRecordingMode !== "off";
			const hasScreenRecording = proctoringOptions.recordScreen;
			const hasAudioRecording = proctoringOptions.audioRecordingMode !== "off";
			const hasScratchNote = proctoringOptions.uploadScratchNotes;
			const tabs = [];

			if (hasWebcamRecording && hasScreenRecording && hasAudioRecording) {
				tabs.push({
					key: "recording",
					icon: <WebcamIcon />,
					label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcamAndScreenRecording"),
					id: getTabIdByType("recording"),
				});
			} else if (hasWebcamRecording && hasScreenRecording) {
				tabs.push({
					key: "recording",
					icon: <WebcamIcon />,
					label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcamAndScreenRecording"),
					id: getTabIdByType("recording"),
				});
			} else if (hasWebcamRecording && hasAudioRecording) {
				tabs.push({
					key: "recording",
					icon: <WebcamIcon />,
					label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcam"),
					id: getTabIdByType("recording"),
				});
			} else if (hasScreenRecording && hasAudioRecording) {
				tabs.push({
					key: "recording",
					icon: <MonitorIcon />,
					label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.screenAndAudio"),
					id: getTabIdByType("recording"),
				});
			} else if (hasWebcamRecording) {
				tabs.push({
					key: "recording",
					icon: <WebcamIcon />,
					label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcam"),
					id: getTabIdByType("recording"),
				});
			} else if (hasScreenRecording) {
				tabs.push({
					key: "recording",
					icon: <MonitorIcon />,
					label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.screen"),
					id: getTabIdByType("recording"),
				});
			} else if (hasAudioRecording) {
				tabs.push({
					key: "recording",
					icon: <MicroPhoneIcon />,
					label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.audio"),
					id: getTabIdByType("recording"),
				});
			}

			if (proctoringOptions?.scanRoom) {
				tabs.push({
					key: "roomScan",
					icon: <RoomScanIcon />,
					label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.roomScan"),
					id: getTabIdByType("roomScan"),
				});
			}
			if (proctoringOptions?.idVerification) {
				tabs.push({
					key: "idPhotos",
					icon: <IdIcon />,
					label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.idPhotos"),
					id: getTabIdByType("idPhotos"),
				});
			}
			if (proctoringOptions?.uploadSignature && !hasTypedSignature) {
				tabs.push({
					key: "signature",
					icon: <SignatureIcon />,
					label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.signature.tab"),
					id: getTabIdByType("signature"),
				});
			}
			if (hasScratchNote) {
				tabs.push({
					key: "scratchNote",
					icon: <ScratchNoteIcon />,
					label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.scratchNote.tab"),
					id: getTabIdByType("scratchNote"),
				});
			}

			if (Object.keys(proctoringOptions).length > 0) {
				tabs.push({
					key: "config",
					icon: <ConfigIcon />,
					label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.configurations"),
					id: getTabIdByType("config"),
				});
			}

			setMainTabList(tabs);
			setMainTab(tabs[0].key as MainTab);
		}
	}, [proctoringOptions, hasTypedSignature]);

	const userInfo = selectedProctoringSession?.userInfo;
	const idPhotoSrc = `/api/verity/photo/${selectedProctoringSession?.uuid}-id.png`;
	const personPhotoSrc = `/api/verity/photo/${selectedProctoringSession?.uuid}-person.png`;

	const sessionStatus = selectedProctoringSession?.status;
	const computeStatusCell = (sessionStatus: string | null | undefined) => {
		let cellValues: { text: string; color: string };
		if (sessionStatus === "flagged" || sessionStatus === "unflagged") {
			cellValues = {
				text: i18n.t("result.components.studentSelection.selectionMenuTSX.complete"),
				color: "#DFF2F2",
			};
		} else if (sessionStatus === "incomplete") {
			cellValues = {
				text: i18n.t("result.components.studentSelection.selectionMenuTSX.incomplete"),
				color: "#F9E4F5",
			};
		} else if (sessionStatus === "unavailable") {
			cellValues = {
				text: i18n.t("result.components.studentSelection.selectionMenuTSX.unavailable"),
				color: "#F8E4DD",
			};
		} else if (sessionStatus === "pending") {
			cellValues = {
				text: i18n.t("result.components.studentSelection.selectionMenuTSX.pending"),
				color: "#E4E3F6",
			};
		} else {
			cellValues = { text: i18n.t("result.components.studentSelection.selectionMenuTSX.error"), color: "#E6E6E6" };
		}
		return (
			<CoreChip
				chipColor={cellValues.color}
				variant="rectangular"
				sx={{
					width: "110px",
					height: "28px",
					alignContent: "center",
					textAlign: "center",
				}}
			>
				{cellValues.text}
			</CoreChip>
		);
	};
	const statusCell = computeStatusCell(sessionStatus);

	let severeCount;
	let majorCount;
	let minorCount;
	let downloadPDFReport;
	if (!isLoading) {
		for (const ev of events) {
			if (!isValidEvent(ev)) {
				continue;
			}
			if (ev.flag_level === 3) {
				severeCount = (severeCount ?? 0) + 1;
			} else if (ev.flag_level === 2) {
				majorCount = (majorCount ?? 0) + 1;
			} else if (ev.flag_level === 1) {
				minorCount = (minorCount ?? 0) + 1;
			}
		}
		severeCount = severeCount ?? 0;
		majorCount = majorCount ?? 0;
		minorCount = minorCount ?? 0;
		let maxFlagTxt = i18n.t("result.components.studentSelection.selectionMenuTSX.none");

		if (severeCount > 0) {
			maxFlagTxt = i18n.t("result.components.studentSelection.selectionMenuTSX.severe");
		} else if (majorCount > 0) {
			maxFlagTxt = i18n.t("result.components.studentSelection.selectionMenuTSX.major");
		} else if (minorCount > 0) {
			maxFlagTxt = i18n.t("result.components.studentSelection.selectionMenuTSX.minor");
		} else {
			maxFlagTxt = i18n.t("result.components.studentSelection.selectionMenuTSX.none");
		}

		const circleRadius = 50;
		const circumference = 2 * Math.PI * circleRadius;
		const totalCount = severeCount + majorCount + minorCount;
		const severeArcLength = ((severeCount / totalCount) * circumference).toFixed(2);
		const severeDasharray = "" + severeArcLength + ", " + circumference.toFixed(2);
		const majorArcLength = ((majorCount / totalCount) * circumference).toFixed(2);
		const majorDasharray = "" + majorArcLength + ", " + circumference.toFixed(2);
		const minorDasharray =
			"" + ((minorCount / totalCount) * circumference).toFixed(2) + ", " + circumference.toFixed(2);

		const severeOffset = "0";
		const majorOffset = severeCount === 0 ? "0" : "-" + severeArcLength;
		const minorOffset =
			severeCount === 0 && majorCount === 0 ? "0" : "-" + (Number(severeArcLength) + Number(majorArcLength)).toFixed(2);

		downloadPDFReport = (
			<DownloadPDFReportButton
				userInfo={userInfo}
				sessionNum={sessionNumber.toString()}
				totalSessions={totalSessions}
				circleRadius={circleRadius}
				severeCount={severeCount}
				severeDasharray={severeDasharray}
				severeOffset={severeOffset}
				majorCount={majorCount}
				majorDasharray={majorDasharray}
				majorOffset={majorOffset}
				minorCount={minorCount}
				minorDasharray={minorDasharray}
				minorOffset={minorOffset}
				maxFlagTxt={maxFlagTxt}
				sessionNotes={sessionNotes}
				webcamVidSources={webcamVidSources}
				webcamTimestampsArr={webcamTimestampsArr}
				duration={duration}
				timeOffset={timeOffset}
				allEvents={events}
				roomScanVidSource={roomScanVidSource}
				roomScanNotes={roomScanNotes}
				roomScanDuration={roomScanDuration}
				roomScanTimestampsArr={roomScanTimestampsArr}
				personPhotoAvailable={Boolean(selectedProctoringSession?.personPhotoAvailable)}
				idPhotoAvailable={Boolean(selectedProctoringSession?.idPhotoAvailable)}
				uuid={selectedProctoringSession?.uuid}
				signatureType={selectedProctoringSession?.signatureType}
			/>
		);
	}

	const [isDeleteConfirmationModalShown, setIsDeleteConfirmationModalShown] = useState<
		"recording" | "roomscan" | undefined
	>();

	const deleteConfirmationModal = (
		<CoreModal
			open={!!isDeleteConfirmationModalShown}
			onClose={() => setIsDeleteConfirmationModalShown(undefined)}
			title={i18n.t("result.components.resultsPage.deleteModal.title")}
			buttons={[
				{
					text: i18n.t("result.components.resultsPage.deleteModal.cancel"),
					color: "secondary",
					onClick: () => {
						setIsDeleteConfirmationModalShown(undefined);
					},
				},
				{
					text: i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.delete"),
					color: "cancel",
					onClick: async () => {
						try {
							isDeleteConfirmationModalShown === "recording"
								? await apiClient.proctoringSession.deleteRecordingsFromSession(sessionUuid)
								: await apiClient.proctoringSession.deleteRoomScanFromSession(sessionUuid);

							setIsDeleteConfirmationModalShown(undefined);

							// update the appropriate deleted flag in props.proctoringSessions without rerendering the parent component.
							props.setProctoringSessions?.((prevSessions) => {
								if (prevSessions) {
									const sessionIndex = prevSessions.findIndex((session) => session.uuid === sessionUuid);
									if (sessionIndex !== -1) {
										const session = prevSessions[sessionIndex];
										if (isDeleteConfirmationModalShown === "recording") {
											session.isWebcamAndScreenCaptureDeleted = true;
										} else {
											session.isRoomScanDeleted = true;
										}
									}
								}
								return prevSessions;
							});
							// sync the UI of the ProctoringResultView to the props.proctoringSessions state
							setSelectedProctoringSession((selectedProctoringSession) => {
								return {
									...selectedProctoringSession,
									...(isDeleteConfirmationModalShown === "recording"
										? { isWebcamAndScreenCaptureDeleted: true }
										: { isRoomScanDeleted: true }),
								};
							});

							if (isDeleteConfirmationModalShown === "recording") {
								prevVolume.current = 0;
								setControlOptions((controlOptions) => ({
									...controlOptions,
									volume: 0,
								}));
							} else {
								prevVolumeRoomScan.current = 0;
								setRoomScanControlOptions((roomScanControlOptions) => ({
									...roomScanControlOptions,
									volume: 0,
								}));
							}

							coreToast.success(i18n.t("result.components.resultsPage.deleteModal.success"));
						} catch (e) {
							setIsDeleteConfirmationModalShown(undefined);
							coreToast.error(i18n.t("result.components.resultsPage.deleteModal.error"));
						}
					},
				},
			]}
			modalVerticalPosition={"top"}
			modalMargin={"25px 0px 0px 0px"}
			keepTopBorderRadius={true}
		>
			<Typography color={"#6B6B6B"}>{i18n.t("result.components.resultsPage.deleteModal.description")}</Typography>
		</CoreModal>
	);

	const [menuShowing, setMenuShowing] = useState(false);
	const [instituteSuffix, setInstituteSuffix] = useState<string | undefined>(undefined);

	const getInstituteSuffix = async () => {
		try {
			const suffix = await apiClient.institute.getSuffix();
			if (suffix) {
				setInstituteSuffix(suffix);
			} else {
				setInstituteSuffix("");
			}
		} catch (error) {
			setInstituteSuffix("");
		}
	};

	useEffect(() => {
		void getInstituteSuffix();
	}, []);

	// Sync video ui on mount
	useEffect(() => {
		setRoomScanHasError(selectedProctoringSession.isRoomScanDeleted);
	}, [selectedProctoringSession.isRoomScanDeleted, selectedProctoringSession.uuid]);

	const assessmentName = removeAssessmentNameSuffix(
		props.assessmentInfo?.name ?? i18n.t("result.components.resultsPage.proctoringResultsViewTSX.unknownAssessment"),
		instituteSuffix,
	);
	const studentName = props.selectedStudent
		? props.studentList[props.studentList.findIndex((student) => student.id === props.selectedStudent)].name
		: i18n.t("result.components.resultsPage.proctoringResultsViewTSX.unnamedStudent");

	const hasRoomScanFinishedPlaying =
		roomScanDuration !== undefined && roomScantimer.timer >= Math.floor(roomScanDuration);

	const objectDetectionCapability = Boolean(
		proctoringOptions && proctoringOptions.objectDetectionMode && proctoringOptions.objectDetectionMode !== "off",
	);
	const faceDetectionCapability = Boolean(
		proctoringOptions && proctoringOptions.faceTrackingMode && proctoringOptions.faceTrackingMode !== "off",
	);
	setObjectDetectionCapability(objectDetectionCapability);
	setFaceDetectionCapability(faceDetectionCapability);
	const showMachineLearningOverlay = objectDetectionCapability || faceDetectionCapability;

	const [courseName, setCourseName] = useState<string>("Unknown");
	useEffect(() => {
		void (async () => {
			const course = await apiClient.course.getCourseInfoFromId(props.courseId);
			if (course.name) {
				setCourseName(course.name);
			}
		})();
	}, [props.courseId]);

	const [optionRelatedInstituteSettings, setOptionRelatedInstituteSettings] =
		useState<OptionRelatedInstituteSettings>();
	const [verityInstituteSettings, setVerityInstituteSettings] = useState<VerityInstituteSettings>();

	useEffect(() => {
		void (async () => {
			if (portalContext.instituteUuid) {
				setOptionRelatedInstituteSettings(await apiClient.institute.getSettings(portalContext.instituteUuid));
			}
			setVerityInstituteSettings(await apiClient.institute.getVeritySettings());
		})();
	}, []);

	const aiSummaryExists = !!aiSummary && Object.keys(aiSummary).length > 0;

	const handleShowAiSummary = () => {
		if (!aiSummaryExists) generateAiSummary();
		setAiSummaryShowing(true);
	};

	const generateAiSummary = async () => {
		if (aiSummaryExists) return;

		try {
			const eventsForSummary = events.map((ev) => {
				const adjustedTime = Number(Math.max(ev.time - timeOffset / 1000, 0).toFixed(2)); // Sync passed time with what's shown in the events table
				const eventLink = processLink(ev);
				const eventDescription = eventLink ? `${processDisplay(ev)} ${eventLink}` : processDisplay(ev);
				return {
					name: ev.name,
					time: adjustedTime,
					flag_level: ev.flag_level,
					description: eventDescription, // Plain language description of the event
				};
			});

			const generatedSummary = await apiClient.proctoringSession.generateAiSummary(sessionUuid, eventsForSummary);
			setAiSummary(generatedSummary);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Grid container sx={{ background: "white", minHeight: "100%" }}>
			<Grid item container md={12} lg={7} gap={"14px"} flexDirection={"column"} paddingBottom={"14px"} height={"100%"}>
				<Grid
					item
					container
					display="flex"
					minHeight="75px"
					alignItems="center"
					justifyContent="space-between"
					paddingX="3%"
					paddingY="1%"
					sx={{ borderBottom: "0.5px solid #CACACA" }}
				>
					<Grid item container xs={12} sm={"auto"} alignItems="center" gap="8px">
						{props.setShowStudentResults && (
							<CoreIconButton
								variant="plain"
								onClick={() => {
									props.setShowStudentResults && props.setShowStudentResults(false);
									props.setSelectedStudent(undefined);
								}}
								sx={{ padding: "4px" }}
								aria-label={i18n.t("result.components.resultsPage.proctoringResultsHeaderTSX.backButtonAriaLabel")}
							>
								<BackButton />
							</CoreIconButton>
						)}
						<Typography
							variant="h1"
							fontWeight={600}
							fontSize="15px"
							style={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								flexGrow: 1,
								maxWidth: "100%",
							}}
						>
							{assessmentName}
						</Typography>
					</Grid>
					<Grid item container xs={12} sm={"auto"} flexShrink={0}>
						{downloadPDFReport ?? <Skeleton width="120px" height="48px" />}
					</Grid>
				</Grid>

				<Grid item container paddingX="3%">
					<CircleGrid severeCount={severeCount} majorCount={majorCount} minorCount={minorCount} />
				</Grid>
				<Grid item container paddingX="3%">
					{mainTabList ? (
						<CoreTabs
							tabs={mainTabList}
							value={mainTab}
							secondary
							height={"36px"}
							onChange={(_, newTab) => {
								setMainTab(newTab);
							}}
							tabsProps={{
								sx: {
									maxWidth: "100%",
								},
								ref: handleMainTabsRef,
							}}
							scrollable
							scrollButtons={mainTabOverflow}
						/>
					) : (
						<Skeleton width={"100%"} height={"48px"} />
					)}
				</Grid>
				<Grid item sx={{ height: "fit-content", minHeight: "60vh", width: "100%", paddingX: "3%" }} id="media_grid">
					{mainTab === "recording" && (
						<>
							<div
								className={!inFullscreen ? "entire_player" : "entire_player_fs"}
								id="entire_player"
								role="tabpanel"
								aria-labelledby={getTabIdByType("recording")}
							>
								{renderVideo ? (
									<ProctoringVideoPlayer
										isFullscreen={inFullscreen}
										sessionId={sessionUuid}
										showTimestamps={showTimestamps}
										controlOptions={controlOptions}
										playback={playback}
										playbackDispatch={playbackDispatch}
										webcamVidSources={webcamVidSources}
										screenVidSources={screenVidSources}
										startDate={startDate}
										duration={duration}
										timeOffset={timeOffset}
										setFullscreen={setFullscreen}
										setPlay={setPlay}
										setVolume={setVolume}
										setSpeed={setSpeed}
										setShowEmailModal={setShowEmailModal}
										setUserEnteredEmail={setUserEnteredEmail}
										setUserEnteredName={setUserEnteredName}
										downloadRequestStatus={downloadRequestStatus}
										setDownloadRequestStatus={setDownloadRequestStatus}
										shouldShowDownload={shouldShowDownload}
										showVideoUnavailable={showVideoUnavailable}
										showAudioThumbnail={showAudioThumbnail}
										showingVideoTitles={true}
										webcamVideoOptions={webcamVideoOptions}
										screencaptureVideoOptions={screencaptureVideoOptions}
										shouldHideCamVid={shouldHideCamVid}
										modifyFSMode={modifyFSMode}
										stringExtend={stringExtend}
										events={events}
										screenTimestampsArr={screenTimestampsArr}
										webcamTimestampsArr={webcamTimestampsArr}
										sessionNotes={sessionNotes}
										visibleLiveNotes={visibleLiveNotes}
										toggleVolume={toggleVolume}
										isWebcamAndScreencaptureDeleted={selectedProctoringSession.isWebcamAndScreenCaptureDeleted}
										deleteConfirmationModal={deleteConfirmationModal}
										setIsDeleteConfirmationModalShown={setIsDeleteConfirmationModalShown}
										hasInstituteEditPermission={userCanEditInstitute || hasInstituteEditPermission}
										sessionStatus={sessionStatus}
										webCamHasError={webCamHasError}
										screenCaptureHasError={screenCaptureHasError}
										setWebCamHasError={setWebCamHasError}
										setScreenCaptureHasError={setScreenCaptureHasError}
										selectedProctoringSessionUuid={selectedProctoringSession.uuid}
										showMachineLearningOverlay={showMachineLearningOverlay}
									/>
								) : (
									<Box width={"100%"} height={"100%"} display={"flex"} alignItems={"center"} justifyContent={"center"}>
										<Skeleton variant="rectangular" width={"90%"} height={"80%"} />
									</Box>
								)}
							</div>
						</>
					)}
					{mainTab === "roomScan" && (
						<>
							<div
								className={!inFullscreen ? "entire_player" : "entire_player_fs"}
								id="entire_player"
								role="tabpanel"
								aria-labelledby={getTabIdByType("roomScan")}
							>
								<div
									className={!inFullscreen ? "left_side" : `left_side_fs`}
									style={showTimestamps ? { width: "fit-content" } : {}}
								>
									<div className={"overlay_container " + (!inFullscreen ? "player_container" : `player_container_fs`)}>
										{!roomScanHasError && (
											<VideoOverlay
												useVideoEndedOverlay={hasRoomScanFinishedPlaying}
												isOverlayVisible={isRoomScanOverlayVisible}
												setPlay={setRoomScanPlay}
											/>
										)}
										<div
											className={!inFullscreen ? "webcam_container" : `webcam_container${stringExtend}`}
											id="roomscan_container"
											style={{ cursor: roomScanHasError ? undefined : "pointer" }}
											onClick={() => {
												if (!roomScanHasError) setRoomScanPlay();
											}}
										>
											<VideoJS
												events={[]}
												debugging_string={"webcam"}
												dispatch={dispatchRoomScan}
												options={roomScanVideoOptions}
												controls={roomScanControlOptions}
												startDate={startDate}
												arrStamps={roomScanTimestampsArr}
												sources={roomScanVidSource}
												videoTimer={roomScantimer}
												displayStateAtom={roomscanDisplayStateAtom}
												toggledTimestamps={showTimestamps}
												isFullscreen={inFullscreen}
												stringExtend={stringExtend}
												showingVideoTitles={false}
												setVideoDuration={setRoomScanDuration}
												isDeleted={selectedProctoringSession.isRoomScanDeleted}
												videoHasError={roomScanHasError}
												setVideoHasError={setRoomScanHasError}
											/>
										</div>
									</div>
									{roomScanVidSource && (
										<div
											className={!inFullscreen ? "control_container" : `control_container_fs`}
											ref={controlContainerRef}
										>
											<VideoControlButton
												aria-label={
													roomScanControlOptions.isPlaying
														? i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.pauseVideoLabel")
														: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.playVideoLabel")
												}
												onClick={() => {
													if (!roomScanHasError) setRoomScanPlay();
												}}
												className={!inFullscreen ? "play_button" : `play_button_fs`}
											>
												<SvgIcon
													sx={controlIconSx}
													component={roomScanControlOptions.isPlaying ? PauseRoundedIcon : PlayArrowRoundedIcon}
												/>
											</VideoControlButton>
											<VolumeControls
												volume={roomScanControlOptions.volume}
												showVolumeInput={!selectedProctoringSession.isRoomScanDeleted}
												onVolumeButtonClick={toggleVolumeRoomScan}
												onVolumeInputChange={setRoomScanVolume}
												inFullscreen={inFullscreen}
											/>

											<div className={!inFullscreen ? "video_time_wrap" : `video_time_wrap_fs`}>
												<span className={!inFullscreen ? "video_time" : `video_time_fs`}>
													{`${formatEventTimestamp(roomScantimer.timer, roomScanDuration)}/${formatEventTimestamp(roomScanDuration ?? 0, roomScanDuration)}`}
												</span>
											</div>
											<Seekbar
												curSeekbarValue={roomScantimer.timer}
												minSeekbarValue={0}
												maxSeekbarValue={roomScanDuration}
												onSeekbarChange={(e) => {
													dispatchRoomScan({
														type: "skip",
														time: e.target.valueAsNumber,
													});
												}}
												inFullscreen={inFullscreen}
											>
												<SeekbarEvents
													events={[]}
													sessionNotes={roomScanNotes}
													liveNotes={[]}
													dispatch={dispatchRoomScan}
													duration={roomScanDuration}
													globaltime={roomScantimer.timer / 1000}
													skip={roomScantimer.skip}
													offsetGetter={0}
													isFullscreen={inFullscreen}
												/>
											</Seekbar>
											<div className={"video_player_icons_wrapper"}>
												<CoreMenuButton
													className={!inFullscreen ? "video-speed-button" : `video-speed-button_fs`}
													menuShowing={showRoomScanVideoSpeed}
													setMenuShowing={setShowRoomScanVideoSpeed}
													style={{ backgroundColor: "#00000000" }}
													icon={<div style={{ color: "white" }}>{roomScanControlOptions.playbackSpeed}x</div>}
													slotProps={{
														menu: {
															transformOrigin: {
																vertical: "bottom",
																horizontal: "center",
															},
															anchorOrigin: {
																vertical: "top",
																horizontal: "center",
															},
															container: controlContainerRef.current,
														},
													}}
													aria-label={i18n.t(
														"result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.playbackSpeed",
														{ speed: roomScanControlOptions.playbackSpeed },
													)}
												>
													{[0.25, 0.5, 1, 1.25, 1.5, 1.75, 2, 4].map((x) => {
														return (
															<CoreMenuItem
																key={x}
																onClick={(_e) => {
																	setRoomScanSpeed(x.toString());
																	setShowRoomScanVideoSpeed(false);
																}}
															>
																<ListItemText primary={`${x}x`} />
															</CoreMenuItem>
														);
													})}
												</CoreMenuButton>
												<div className={!inFullscreen ? "fullscreen_button" : `fullscreen_button_fs`}>
													<VideoControlButton
														className={!inFullscreen ? "actual_fullscreen_button" : `actual_fullscreen_button_fs`}
														onClick={() => setFullscreen()}
														aria-label={
															inFullscreen
																? i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.exitFullscreen")
																: i18n.t(
																		"result.components.studentSelection.videoPlayer.videoPlayerTSX.enterFullscreen",
																	)
														}
													>
														<SvgIcon
															sx={[controlIconSx]}
															component={inFullscreen ? ExitFullscreenIcon : FullscreenRoundedIcon}
														/>
													</VideoControlButton>
												</div>
												{Object.entries(roomScanVidSource).length > 0 &&
													!selectedProctoringSession.isRoomScanDeleted &&
													!inFullscreen &&
													(userCanEditInstitute || hasInstituteEditPermission) &&
													sessionStatus !== "incomplete" && (
														<CoreMenuButton
															aria-label={i18n.t(
																"result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.moreOptions",
															)}
															className="download_button"
															menuShowing={menuShowing}
															setMenuShowing={setMenuShowing}
															slotProps={{
																menu: {
																	transformOrigin: {
																		vertical: "bottom",
																		horizontal: "center",
																	},
																	anchorOrigin: {
																		vertical: "top",
																		horizontal: "center",
																	},
																},
															}}
															style={{ backgroundColor: "#17171D" }}
															icon={<SvgIcon sx={controlIconSx} component={Action} />}
														>
															<CoreMenuItem
																onClick={() => {
																	setIsDeleteConfirmationModalShown("roomscan");
																	setMenuShowing(false);
																}}
															>
																<ListItemText
																	primary={i18n.t(
																		"result.components.resultsPage.proctoringResultsViewTSX.deleteRecording",
																	)}
																/>
															</CoreMenuItem>
														</CoreMenuButton>
													)}
											</div>
										</div>
									)}
								</div>
							</div>
						</>
					)}
					{mainTab === "idPhotos" && (
						<Grid
							item
							container
							aria-labelledby={getTabIdByType("idPhotos")}
							role="tabpanel"
							className={"id-photo-tabpanel"}
						>
							<Box className={"id-photo-container"}>
								<Typography variant="body1" fontWeight={600} color={"#FFFFFF"}>
									{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.idPhoto")}
								</Typography>
								<img
									src={idPhotoSrc}
									alt={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.idPhotoAlt")}
									onError={handleImgError}
									className={"id-photo-image"}
								/>
							</Box>
							<Box className={"id-photo-container"}>
								<Typography variant="body1" fontWeight={600} color={"#FFFFFF"}>
									{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.verificationPhoto")}
								</Typography>
								<img
									src={personPhotoSrc}
									alt={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.verificationPhotoAlt")}
									onError={handleImgError}
									className={"id-photo-image"}
								/>
							</Box>
						</Grid>
					)}
					{mainTab === "signature" && !hasTypedSignature && (
						<SignatureTabPanel
							imageURL={fetchSignatureHook.value}
							signatureType={selectedProctoringSession.signatureType}
						/>
					)}
					{mainTab === "scratchNote" && <ScratchNote images={fetchLinksHook.value} />}
					{mainTab === "config" && (
						<ProctoringConfigView
							proctoringAssessmentConfiguration={proctoringOptions}
							applications={applications}
							extensions={extensions}
							instituteSettings={instituteSettings}
							verityInstituteSettings={verityInstituteSettings}
							optionRelatedInstituteSettings={optionRelatedInstituteSettings}
						/>
					)}
				</Grid>
			</Grid>
			<Grid
				item
				md={12}
				lg={5}
				sx={{
					borderLeft: { lg: "0.5px solid #CACACA", md: "none" }, // Apply border-left at md and above
					borderTop: { xs: "0.5px solid #CACACA", md: "0.5px solid #CACACA", lg: "none" }, // Apply border-top at xs and below
				}}
				container
				flexDirection={"column"}
			>
				<Grid
					item
					container
					display="flex"
					minHeight="75px"
					alignItems="center"
					justifyContent={props.studentList.length > 1 ? "space-between" : "center"}
					paddingX="3%"
					paddingY="20px"
					sx={{ borderBottom: "0.5px solid #CACACA" }}
					direction={{ xs: "column", md: "row" }}
					width={"100%"}
					gap={"3px"}
				>
					{/* Wrapper for buttons and select */}
					{props.studentList.length > 1 && (
						<CoreButton
							sx={{ height: "32px", width: "95px" }}
							onClick={() => {
								const currentIndex = props.studentList.findIndex((student) => student.id === props.selectedStudent);
								const nextIndex = currentIndex - 1 < 0 ? props.studentList.length - 1 : currentIndex - 1;
								props.setSelectedStudent(props.studentList[nextIndex].id);
							}}
							startIcon={<ArrowIcon style={{ transform: "rotate(180deg)", height: "14px" }} />}
							applyColorToSvg
						>
							{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.previous")}
						</CoreButton>
					)}
					<CoreSelect
						sx={{
							fontWeight: 600,
							fontSize: "20px",
							height: "40px",
							maxWidth: { md: "calc(100% - 200px)", sm: "100%", xs: "100%" },
							border: "none",
							"& .MuiSelect-icon": {
								top: "11px",
							},
							"& .MuiOutlinedInput-notchedOutline": {
								border: "none",
							},
							"&:hover .MuiOutlinedInput-notchedOutline": {
								border: "none",
							},
						}}
						label={i18n.t("result.components.resultsPage.ariaLabels.studentComboBox")}
						value={props.selectedStudent}
						onChange={(event) => {
							const studentId = Number(event.target.value);
							props.setSelectedStudent(studentId);
						}}
						renderValue={(selectedValue) => {
							const student = props.studentList.find((s) => s.id === selectedValue);
							if (!student) return null;
							return <StudentOptionDisplay name={student.name} flagLevel={student.severityLevel ?? 0} />;
						}}
					>
						{props.studentList.map((student, index) => (
							<CoreMenuItem key={index} value={student.id}>
								<StudentOptionDisplay name={student.name} flagLevel={student.severityLevel ?? 0} />
							</CoreMenuItem>
						))}
					</CoreSelect>

					{props.studentList.length > 1 && (
						<CoreButton
							sx={{ height: "32px", width: "95px" }}
							onClick={() => {
								const currentIndex = props.studentList.findIndex((student) => student.id === props.selectedStudent);
								const nextIndex = (currentIndex + 1) % props.studentList.length;
								props.setSelectedStudent(props.studentList[nextIndex].id);
							}}
							disabled={props.studentList.length <= 1}
							endIcon={<ArrowIcon style={{ marginLeft: "8px", height: "14px" }} />}
							applyColorToSvg
						>
							{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.next")}
						</CoreButton>
					)}
				</Grid>
				<Grid item container paddingX={"20px"} paddingTop="14px" gap="14px" display={"flex"} flexDirection={"column"}>
					<Grid item container alignItems="center" gap={"6px"} minHeight="30px">
						<Typography variant="h2" fontWeight={500} fontSize="15px">
							{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.attemptStatus")}
						</Typography>
						{statusCell}
					</Grid>
					<CoreSelect
						sx={{
							maxWidth: "100%",
							width: "100%",
							height: "35px",
							fontSize: "15px",
							fontWeight: "400",
							padding: 0,
							display: "flex",
							alignItems: "center",
						}}
						value={sessionNumber - 1}
						onChange={(event) => {
							const index = Number(event.target.value);
							setSelectedProctoringSession(props.proctoringSessions[index]);
							setSessionNumber(index + 1);
							setSessionUuid(props.proctoringSessions[index].uuid);
						}}
						label={i18n.t("result.components.resultsPage.ariaLabels.attemptComboBox")}
						renderValue={() =>
							i18n.t("result.components.resultsPage.proctoringResultsViewTSX.attempt", {
								current: sessionNumber,
								total: totalSessions,
							})
						}
						MenuProps={{
							MenuListProps: {
								onKeyDown: (event) => {
									if (event.key === "Tab") {
										event.stopPropagation(); // Allows tab navigation
									}
								},
							},
						}}
					>
						{props.proctoringSessions.map((_, index) => (
							<CoreMenuItem key={index} value={index}>
								<Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
									{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.attempt2", {
										attempt: index + 1,
										total: totalSessions,
									})}
									{computeStatusCell(props.proctoringSessions[index].status)}
								</Box>
							</CoreMenuItem>
						))}
					</CoreSelect>
					{mainTab !== "roomScan" && (
						<>
							{rightTabList.length > 1 && (
								<CoreTabs
									tabs={rightTabList}
									width="100%"
									tabProps={{ sx: { width: `max(calc(100%/${rightTabList.length}), 70px)` } }}
									height={"48px"}
									value={tab}
									onChange={(_, newTab) => {
										setTab(newTab);
										if (newTab === "notes") {
											getSessionNotesHandler();
										}
									}}
								/>
							)}
							<div style={{ width: "100%" }}>
								{tab === "flaggedEvents" &&
									(aiSummaryShowing ? (
										<AiSummary summaryData={aiSummary} closeSummary={() => setAiSummaryShowing(false)} />
									) : (
										<TimestampLinks
											sessionId={sessionUuid}
											events={events}
											dispatch={playbackDispatch}
											setPlay={setPlay}
											debugger={"webcam"}
											options={videoOptionsFrame}
											controls={controlOptions}
											arrStamps={webcamTimestampsArr}
											sources={webcamVidSources}
											globaltime={playback.timer / 1000}
											skip={playback.skip}
											offsetSetter={setTimeOffset}
											offsetGetter={timeOffset}
											isLoading={isLoading}
											mediaGridHeight={mediaGridHeight}
											assessmentName={assessmentName}
											studentName={studentName}
											duration={duration ?? 0}
											updateEvents={updateEvents}
											courseName={courseName}
											attemptNumber={sessionNumber}
											assessmentStatus={selectedProctoringSession.status ?? "Unknown"}
											recordingDate={firstEventDate?.toLocaleDateString()}
											startDate={startDate}
											hasTabs={rightTabList.length > 1}
											aiSummaryExists={aiSummaryExists}
											showAiSummary={!aiSummaryLoading ? handleShowAiSummary : undefined}
										/>
									))}
								{tab === "notes" && (
									<div role="tabpanel" className={"notes-tabpanel"} aria-labelledby={getTabIdByType("notes")}>
										<div
											style={{
												display: "flex",
												flexBasis: "100%",
												height: "78%",
												overflow: "auto",
												justifyContent: "center",
											}}
										>
											{sessionNotes.length === 0 ? (
												<NoResults
													text={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.noNotesAdded")}
													add={true}
												/>
											) : (
												<div className={"side-bar-content-notes"}>
													{sessionNotes.map((v, i) => (
														<SessionNoteRow
															key={i}
															sessionNote={v}
															thumbnails={noteThumbnails}
															offsetGetter={timeOffset}
															dispatch={playbackDispatch}
															editNote={editNoteHandler}
															deleteNote={deleteNoteHandler}
															duration={duration ?? 0}
														/>
													))}
												</div>
											)}
										</div>
										<div style={{ display: "flex", flexBasis: "100%", padding: "20px" }}>
											<CoreTextBox
												placeholder={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.enterNotes")}
												height={"45px"}
												onKeyDown={(e) => e.stopPropagation()}
												onChange={(e) => setSideBarNotesNewInput(e.target.value)}
												value={sideBarNotesNewInput}
												disabled={waitingAddNote}
												endAdornment={
													<button
														className="side-bar-notes-textarea-button"
														style={{ cursor: waitingAddNote ? "default" : "pointer" }}
														aria-label={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.saveNotes")}
														onClick={() => {
															setWaitingAddNote(true);
															addNoteHandler(sideBarNotesNewInput)
																.then((success) => {
																	if (success) {
																		setSideBarNotesNewInput("");
																		getSessionNotesHandler();
																	} else {
																		coreToast.error(
																			i18n.t(
																				"result.components.studentSelection.videoPlayer.videoPlayerTSX.addNoteFail",
																			),
																		);
																	}
																})
																.catch(() => {
																	coreToast.error(
																		i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.addNoteFail"),
																	);
																})
																.finally(() => {
																	setWaitingAddNote(false);
																});
														}}
														disabled={waitingAddNote}
													>
														<PaperPlaneIcon style={{ width: "28px", height: "28px" }} />
													</button>
												}
												style={{
													fontSize: "14px",
													width: "100%",
													height: "47px",
													borderRadius: "8px",
												}}
											/>
										</div>
									</div>
								)}
								{tab === "liveNotes" && (
									<div
										style={{
											display: "flex",
											width: "100%",
											flexWrap: "wrap",
											height: "62vh",
											border: "1px solid #e6e0e9",
											borderRadius: "10px",
										}}
									>
										<div
											style={{
												display: "flex",
												flexBasis: "100%",
												overflow: "auto",
												justifyContent: "center",
											}}
										>
											{visibleLiveNotes.length === 0 ? (
												<NoResults
													text={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.noNotesAdded")}
													add={true}
												/>
											) : (
												<div className={"side-bar-content-notes"}>
													{visibleLiveNotes.map((v, i) => (
														<SessionNoteRow
															key={i}
															sessionNote={v}
															thumbnails={noteThumbnails}
															offsetGetter={timeOffset}
															dispatch={playbackDispatch}
															duration={duration ?? 0}
														/>
													))}
												</div>
											)}
										</div>
									</div>
								)}
							</div>
						</>
					)}
					{mainTab === "roomScan" && (
						<div style={{ display: "flex", height: "68vh", flexDirection: "column", gap: "14px" }}>
							<div
								style={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									height: "40px",
								}}
							>
								<div style={{ display: "flex" }}>
									<NotesIcon />
								</div>
								<div
									style={{
										fontSize: "16px",
										marginLeft: "10px",
										fontWeight: "600",
									}}
								>
									{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.roomScanNotes")}
								</div>
							</div>
							<div
								style={{
									display: "flex",
									width: "100%",
									height: "100%",
									border: "1px solid #e6e0e9",
									borderRadius: "10px",
									flexDirection: "column",
								}}
							>
								<div
									style={{
										display: "flex",
										flexBasis: "100%",
										overflow: "auto",
										justifyContent: "center",
										flexWrap: "wrap",
										alignContent: "flex-start",
										marginTop: "10px",
										height: "80%",
									}}
								>
									{roomScanNotes.length === 0 ? (
										<NoResults
											text={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.noNotesAdded")}
											add={true}
										/>
									) : (
										<div className={"side-bar-content-notes"}>
											{roomScanNotes.map((v, i) => (
												<SessionNoteRow
													key={i}
													sessionNote={v}
													thumbnails={roomScanNoteThumbnails}
													offsetGetter={0}
													dispatch={dispatchRoomScan}
													editNote={editRoomScanNoteHandler}
													deleteNote={deleteRoomScanNoteHandler}
													duration={roomScanDuration ?? 0}
												/>
											))}
										</div>
									)}
								</div>
								<div
									style={{
										display: "flex",
										padding: "20px",
										flexWrap: "wrap",
										alignContent: "flex-end",
										height: "50px",
									}}
								>
									<CoreTextBox
										placeholder={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.enterNotes")}
										className={"side-bar-notes-textarea"}
										height={"45px"}
										onKeyDown={(e) => e.stopPropagation()}
										onChange={(e) => setSideBarRoomScanNotesNewInput(e.target.value)}
										value={sideBarRoomScanNotesNewInput}
										disabled={waitingAddNote}
										style={{
											fontSize: "14px",
											width: "100%",
											outline: "none",
											height: "47px",
											borderRadius: "8px",
										}}
										endAdornment={
											<button
												className="side-bar-notes-textarea-button"
												style={{ cursor: waitingAddNote ? "default" : "pointer" }}
												onClick={() => {
													setWaitingAddNote(true);
													addRoomScanNoteHandler(sideBarRoomScanNotesNewInput)
														.then((success) => {
															if (success) {
																setSideBarRoomScanNotesNewInput("");
																getRoomScanNotesHandler();
															} else {
																coreToast.error(
																	i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.addNoteFail"),
																);
															}
														})
														.catch(() => {
															coreToast.error(
																i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.addNoteFail"),
															);
														})
														.finally(() => {
															setWaitingAddNote(false);
														});
												}}
												disabled={waitingAddNote}
											>
												{waitingAddNote ? (
													<CoreSpinner color="secondary" size="24px" />
												) : (
													<PaperPlaneIcon style={{ width: "28px", height: "28px" }} />
												)}
											</button>
										}
									/>
								</div>
							</div>
						</div>
					)}
				</Grid>
			</Grid>
			<CoreModal
				maxWidth="min(50%, 600px)"
				open={showEmailModal}
				onClose={() => setShowEmailModal(false)}
				title={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.emailRecording")}
				buttons={[
					{
						text: i18n.t("result.components.resultsPage.proctoringResultsViewTSX.cancel"),
						color: "secondary",
						onClick: () => {
							setShowEmailModal(false);
						},
					},
					{
						text: i18n.t("result.components.resultsPage.proctoringResultsViewTSX.send"),
						color: "primary",
						onClick: () => {
							setDownloadRequestStatus("sending");
							setShowEmailModal(false);
							apiClient.download
								.requestProctoringSessionRecordingDownload(sessionUuid, userEnteredEmail, userEnteredName)
								.then(({ ready, emailDestination }) => {
									setDownloadRequestStatus({ status: "sent", ready, emailDestination });
								})
								.catch((err) => {
									console.error(err);
									setDownloadRequestStatus("failed");
								});
						},
					},
				]}
			>
				<div>
					<Typography>{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.receiverInfoTxt")}</Typography>

					<div style={{ marginTop: "10px" }}>
						<Typography
							style={{
								fontSize: "15px",
								lineHeight: "24.38px",
								color: "black",
								marginBottom: "10px",
								fontWeight: "bold",
							}}
						>
							{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.fullName")}
						</Typography>
						<CoreTextField
							required
							sx={{ width: "100%" }}
							type="name"
							value={userEnteredName}
							onChange={(e) => {
								setUserEnteredName(e.target.value);
							}}
						/>
					</div>
					<div style={{ marginTop: "10px" }}>
						<Typography
							style={{
								fontSize: "15px",
								lineHeight: "24.38px",
								color: "black",
								marginBottom: "10px",
								fontWeight: "bold",
							}}
						>
							{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.email")}
						</Typography>
						<CoreTextField
							required
							sx={{ width: "100%" }}
							type="email"
							value={userEnteredEmail}
							onChange={(e) => {
								setUserEnteredEmail(e.target.value);
							}}
						/>
					</div>
				</div>
			</CoreModal>
			{isDeleteConfirmationModalShown && deleteConfirmationModal}
		</Grid>
	);
};

export default ProctoringResultView;
